/* ============================================================================
   Precio base. A qué precio convocar una compra.

   Las otras pantallas miden cuánto cuesta la canasta. Ésta contesta una sola
   pregunta operativa: a qué precio convoco la compra de ESTE producto, en ESTA
   presentación, y qué tan buena es la oferta que ya me llegó. El número que
   entrega se mete a un acta, así que va siempre pegado a las tres cosas que lo
   sostienen —cuántas observaciones, en cuántas entidades, qué tan ancha es la
   nube— y a la fecha de la última observación de la fuente.

   Tres límites gobiernan el diseño y los tres se dicen arriba, antes de
   cualquier cifra:

   · **Esto no es la canasta del INEGI.** Contra el boletín, esta fuente se
     desvía en mediana más de veinte por ciento por renglón. Las dos cifras
     miden cosas distintas y en ninguna parte se restan ni se promedian. Los
     conteos de esa comparación salen de `retail.contrasteInegi` y se recalculan
     en cada corrida: aquí no se teclea ninguno. La lección es literal —«las
     doce clases SCIAN» resultaron ser trece y la frase llevaba meses
     mintiendo—.

   · **El veredicto se pinta antes que el número, y en los tres casos sin número
     no hay número.** De las presentaciones que publica el pipeline, la mayoría
     no tiene margen calculable contra el anaquel: o el autoservicio no vende
     esa presentación, o la vende pero no es el mismo bien, o los dos canales no
     coinciden en suficientes entidades. La pantalla lo declara; no lo fabrica
     con otra presentación.

   · **Con la celda fuera de la compuerta no hay punto: hay rango y motivo.**
     Ahí `PriceBand` no dibuja la regla de la mediana y el evaluador de ofertas
     se deshabilita, porque calificar una oferta contra una mediana que no
     representa a nadie es peor que no calificarla.

   NO recibe `ambito`. QQP se desglosa por canal y entidad, no por rural y
   urbano; pasarle un ámbito fingiría un filtro que no existe. Es el mismo
   razonamiento con el que `Territorio` y `Abasto` tampoco lo reciben.

   Una decisión que no es evidente y que gobierna medio archivo: **todo lo que
   se calcula con una regla del pipeline —compuerta R4, intervalo R5, margen
   pareado R8, deriva R12— existe únicamente en la banda publicada**, que es el
   corte nacional del mes de referencia. Los demás cortes se leen del archivo de
   detalle, que trae los cuantiles pero no `ciLo`/`ciHi` —`RETAIL_FIELDS` no los
   declara— y por eso no se dibujan como banda de precio: `PriceBand` explicaría
   la falta de intervalo diciendo que la n no alcanza, y con 62 observaciones eso
   sería falso. Rehacer aquí esas reglas duplicaría umbrales que ya viven en un
   solo lugar.
   ========================================================================== */

import { Fragment, useEffect, useMemo, useRef, useState } from 'react'
import { BarList, type BarRow } from '../charts/BarList'
import { LineChart, type ChartSeries } from '../charts/LineChart'
import { PriceBand } from '../charts/PriceBand'
import { Sparkline } from '../charts/Sparkline'
import {
  Callout,
  Card,
  FilterBar,
  FilterField,
  HeroFigure,
  Section,
  Select,
} from '../components/ui'
import { StatTile } from '../components/StatTile'
import { dataset } from '../data/dataset'
import {
  DERIVA_R2_MINIMO,
  OFERTA_BULTO,
  OFERTA_BULTO_LITROS,
  OFERTA_LIMITES,
  OFERTA_SIN_BASE,
  VIGENCIA_DESFASE,
  VIGENCIA_TOPE_DIAS,
  chainRankingCommonStates,
  derivaAcumuladaPct,
  desviacionGeografica,
  diasEntreIso,
  hoyIsoLocal,
  retailBand,
  retailMarkers,
  scoreOffer,
  serieEstable,
  sortRetailRows,
  vigenciaHasta,
  type RetailRow,
  type SerieEstable,
  type Vigencia,
} from '../data/derive'
import {
  RETAIL_CHANNEL_LABEL,
  RETAIL_NOT_CANASTA_WARNING,
  RETAIL_OVERLAP_WARNING,
  type PriceBand as PriceBandData,
  type PriceCell,
  type RetailChannel,
  type RetailComparator,
  type RetailReference,
  type SeriesPoint,
} from '../data/schema'
import {
  currency,
  formatDate,
  formatPeriodLong,
  integer,
  number,
  percent,
  percentDelta,
  pluralize,
  type Period,
} from '../lib/format'
import './precioBase.css'

/* --- Catálogos declarados ------------------------------------------------ */

/**
 * Los renglones que BAMX licita por tonelada, en el orden en que se ofrecen.
 *
 * Es juicio editorial —de la operación de compra de la Red, no del dato— y por
 * eso vive declarado con clave ENIGH, igual que `RETAIL_CATALOG` en el
 * pipeline. Su longitud nunca se escribe en prosa: el grupo se arma filtrando
 * contra las referencias que el dataset trae, así que si el INEGI reclasifica
 * una clave el grupo se encoge solo en vez de prometer una opción que no
 * existe. El primero es también el renglón por omisión de la vista.
 */
const RENGLONES_POR_TONELADA: readonly string[] = ['A137', 'A019', 'A173', 'A095', 'A093']

const GRUPO_TONELADA = 'Lo que BAMX compra por tonelada'

/**
 * Rótulo legible de cada cadena del ranking.
 *
 * El dataset trae `chainId` en kebab (`bodega-aurrera`) y no publica catálogo de
 * nombres: `CHAIN_FAMILIES` se queda en el pipeline. Este mapa es el puente y
 * lleva respaldo tipográfico para que una cadena nueva aparezca legible en vez
 * de desaparecer. Desaparecer en silencio es justo lo que vigila la guarda del
 * pipeline: «Central de Abastos» → «Central de Abasto» ya pasó una vez y partió
 * una serie a la mitad.
 */
const CADENA_ROTULO: Record<string, string> = {
  'bodega-aurrera': 'Bodega Aurrerá',
  chedraui: 'Chedraui',
  soriana: 'Soriana',
  walmart: 'Walmart',
  'la-comer': 'La Comer',
  heb: 'HEB',
  tortillerias: 'Tortillerías',
  'mercado-publico': 'Mercado público',
}

function rotuloCadena(chainId: string): string {
  const declarado = CADENA_ROTULO[chainId]
  if (declarado !== undefined) return declarado
  return chainId
    .split('-')
    .map((parte) => `${parte.charAt(0).toUpperCase()}${parte.slice(1)}`)
    .join(' ')
}

/* Aquí vivía `MIN_OBS_ENTIDAD = 4`, el mínimo de observaciones para que una
   entidad entrara a la lista de §4.6. Era un umbral inventado: el archivo de
   detalle nunca publica una celda por debajo de su propio piso —medido, el `n`
   más chico de las 9 117 filas por entidad es 5, que es la guarda G10 del
   pipeline—, así que el filtro no descartaba jamás una fila y la vista no podía
   saber si alguna entidad se había quedado en 4. Aun así el mensaje vacío y el
   pie afirmaban ese umbral, y con el chile poblano la misma pantalla decía «33
   observaciones en 11 entidades» arriba y «0 entidades · ninguna entidad llega a
   4 observaciones» cuatro secciones abajo. La ausencia se rotula por lo que es:
   de las entidades con observación que declara la celda nacional, cuántas
   publica el archivo, y cuál es el piso medido de ese archivo. */

/** Dispersión entre entidades a partir de la cual se desaconseja el precio nacional. */
const DISPERSION_MAX_PCT = 60

/**
 * Largo máximo, en caracteres, de los dos campos del evaluador.
 *
 * No es un límite de negocio —doce dígitos son novecientos mil millones de
 * pesos por kilo— sino de maquetación: el veredicto repite la oferta con
 * separadores de millar y esa cadena no tiene dónde romperse, así que empuja el
 * ancho del documento entero. Medido a 320 px, el ancho que soporta §4.10: con
 * 12 caracteres el documento mide lo mismo que con una oferta de dos dígitos;
 * con 15 crece 33 px, con 20 crece 139 px y con los 309 dígitos de 1e308 llega
 * a 3 062 px y toda la página se desplaza en horizontal.
 */
const OFERTA_MAX_CARACTERES = 12

/** Las dos series de la gráfica mensual. Dos colores del sistema, no más. */
const CANAL_SERIE: readonly { channel: RetailChannel; color: string }[] = [
  { channel: 'central', color: 'var(--series-1)' },
  { channel: 'autoservicio', color: 'var(--series-3)' },
]

/** Clave de la geografía nacional, la misma que escribe el pipeline. */
const NACIONAL = 'MX'

/* --- Formato local ------------------------------------------------------- */

const ORDINAL_QUINCENA: Record<string, string> = { '1': 'primera', '2': 'segunda' }

const MESES = [
  'enero',
  'febrero',
  'marzo',
  'abril',
  'mayo',
  'junio',
  'julio',
  'agosto',
  'septiembre',
  'octubre',
  'noviembre',
  'diciembre',
] as const

/**
 * «05-2026_Q2» → «segunda de mayo».
 *
 * El sufijo cambió de `_01/_02` a `_Q1/_Q2` entre dos publicaciones de Profeco,
 * así que la expresión acepta las dos formas. Si no empata devuelve la cadena
 * cruda: rotular mal una quincena es peor que enseñar el identificador.
 */
function quincenaLarga(quincena: string): string {
  const m = /^(\d{2})-(\d{4})_(?:0?([12])|Q([12]))$/.exec(quincena)
  if (m === null) return quincena
  const mes = MESES[Number(m[1]) - 1]
  const orden = ORDINAL_QUINCENA[m[3] ?? m[4] ?? '']
  if (mes === undefined || orden === undefined) return quincena
  return `${orden} de ${mes}`
}

function capitalizar(texto: string): string {
  return `${texto.charAt(0).toUpperCase()}${texto.slice(1)}`
}

/** «pesos por kilo» / «pesos por litro». El denominador vive en la referencia. */
function unidadLarga(ref: RetailReference): string {
  return ref.denominador === 'L' ? 'pesos por litro' : 'pesos por kilo'
}

/** «por kilo» / «por litro», para colgarlo de una cifra. */
function porUnidad(ref: RetailReference): string {
  return ref.denominador === 'L' ? 'por litro' : 'por kilo'
}

/**
 * Porcentaje con signo, o la ausencia dicha en palabras.
 *
 * Existe para no escribir `?? 0` al imprimir un extremo del intervalo: un cero
 * ahí diría «el intervalo llega justo al empate», que es una afirmación, y lo
 * que pasa es que no hay dato.
 */
function pctOsinDato(valor: number | null): string {
  return valor === null ? 'sin dato' : percentDelta(valor)
}

/**
 * Lee un campo de texto como número positivo, o `null`.
 *
 * `null` y no `0`: el estado inicial de un `input` vacío es la cadena vacía y
 * `Number('')` vale 0 —que cae por debajo del p10 de cualquier referencia y
 * saldría coronado como «Excepcional»—. Se quitan las comas de millares porque
 * `es-MX` las escribe y `Number('1,000')` es `NaN`.
 */
function parsearNumero(texto: string): number | null {
  const limpio = texto.trim().replace(/,/g, '')
  if (limpio === '') return null
  const v = Number(limpio)
  return Number.isFinite(v) && v > 0 ? v : null
}

/** «bajando desde enero de 2026». En minúsculas: es una aposición, no una frase. */
function rumboDeriva(deriva: number, primerMes: Period | null): string {
  const rumbo = deriva > 0 ? 'subiendo' : deriva < 0 ? 'bajando' : 'plano'
  return primerMes === null ? rumbo : `${rumbo} desde ${formatPeriodLong(primerMes)}`
}

/**
 * Prosa de la deriva. Sin ajuste suficiente NO se dice que hay tendencia (R12).
 *
 * La serie plana se contesta ANTES de mirar el R², porque en esas cuatro
 * referencias el 0.00 que trae el dataset es `0/0` —varianza total cero, nada
 * que explicar— y no una medida de ajuste. Presentarlo como ajuste ponía el
 * precio más estable del dataset («$22.00 los cinco meses») bajo la misma
 * advertencia que un precio que brinca sin rumbo.
 */
function fraseDeriva(
  deriva: number | null,
  r2: number | null,
  primerMes: Period | null,
  estable: SerieEstable | null,
): string {
  if (estable !== null) {
    return `Sin cambio: la mediana mensual en central de abasto se quedó en ${currency(estable.valor)} los ${integer(estable.meses)} meses de la serie, de ${formatPeriodLong(estable.desde)} a ${formatPeriodLong(estable.hasta)}. El R² que trae el dataset sale de dividir cero entre cero y no mide ningún ajuste.`
  }
  if (deriva === null || r2 === null) {
    return 'Sin serie suficiente para estimar una tendencia en central de abasto.'
  }
  if (r2 < DERIVA_R2_MINIMO) {
    return `Sin tendencia clara: el ajuste de la recta es de R² ${number(r2, 2)} y por debajo de ${number(DERIVA_R2_MINIMO, 1)} la deriva no se presenta como tendencia.`
  }
  return `${capitalizar(rumboDeriva(deriva, primerMes))} (R² ${number(r2, 2)}).`
}

/**
 * Lo que se le añade a la deriva cuando el lector NO está en el corte publicado.
 *
 * La serie mensual es nacional y no tiene desglose por entidad —la gráfica de
 * §4.8 lo declara en su propio subtítulo—, así que la deriva no cambia al
 * elegir una entidad ni al elegir otro mes. Antes se apagaba: con «Entrega en:
 * Ciudad de México» la papa decía «sin tendencia clara · revise cada mes», que
 * es el texto que R12 reserva para R² < 0.5, mientras tres bloques abajo la
 * misma pantalla dibujaba $17.50 en enero contra $38.00 en mayo, +20.6 % al mes
 * con R² 0.95. Se muestra la deriva nacional, rotulada como tal.
 */
const DERIVA_ES_NACIONAL =
  'Se estima sobre la serie mensual nacional en central de abasto, la misma que dibuja la gráfica ' +
  'de abajo: ni «Entrega en» ni el mes la mueven, porque la fuente publica el desglose por entidad ' +
  'sólo del mes y no de la serie.'

/**
 * Lo mismo para la vigencia, que sale de esa deriva y del precio base publicado.
 */
function vigenciaEsNacional(mesReferencia: Period): string {
  return `La ventana corre desde la última observación de la fuente y describe el precio base publicado —nacional, ${formatPeriodLong(mesReferencia)}—, no este corte.`
}

/** El renglón «Diferencial contra anaquel» de la ficha. */
function fraseMargen(banda: PriceBandData): string {
  const m = banda.margin
  if (m.pct === null) {
    return `NO APLICA. ${m.reason ?? 'El diferencial no se puede publicar para esta presentación.'}`
  }
  const canal =
    banda.anaquel === null ? 'anaquel' : RETAIL_CHANNEL_LABEL[banda.anaquel.channel].toLowerCase()
  const ic =
    m.ciLo === null || m.ciHi === null
      ? ''
      : ` (IC 95 %: ${percentDelta(m.ciLo)} a ${percentDelta(m.ciHi)})`
  return `${percentDelta(m.pct)} en ${canal} contra central${ic}, sobre ${integer(m.states)} ${pluralize(m.states, 'entidad pareada', 'entidades pareadas')}.`
}

/**
 * «65 días de antigüedad», contados hoy contra la última observación.
 *
 * No sale de `retail.ageDays`: el pipeline congela ese campo con el reloj de la
 * corrida —el dataset trae 64, de la corrida del 1 de agosto de 2026— y la
 * página se lee después. §4.0 pide los días «calculados de `lastObservation`».
 * El `null` es la fecha ilegible y se dice; un cero afirmaría que el dato es de
 * hoy, que es la mentira que esta pantalla existe para no decir.
 */
function fraseAntiguedad(dias: number | null): string {
  return dias === null
    ? 'antigüedad no calculable: la fuente no trae una fecha legible'
    : `${integer(dias)} ${pluralize(dias, 'día', 'días')} de antigüedad`
}

/** Pie de la banda: fuente, licencia y la fecha que envejece la cifra. */
function pieDeBanda(retail: RetailComparator, mes: Period, diasDeRezago: number | null): string {
  return `Fuente: Profeco, Quién es Quién en los Precios (CC BY 4.0), ${formatPeriodLong(mes)}. Última observación en la fuente: ${formatDate(retail.lastObservation)}, ${fraseAntiguedad(diasDeRezago)}.`
}

/* --- Vigencia (§4.4 y §4.9) ---------------------------------------------- */

interface ProsaVigencia {
  /** El valor grande de la casilla. */
  valor: string
  /** La nota al pie de la casilla. */
  nota: string
  /** El renglón de la ficha, que es también el del CSV. */
  acta: string
}

/**
 * Las tres formas de decir la vigencia, en una sola función.
 *
 * Viven juntas porque la casilla, la ficha del acta y el CSV imprimían la misma
 * fecha por tres caminos distintos y los tres se equivocaban igual: la ventana
 * se contaba desde hoy y no desde la última observación de la fuente.
 *
 * Con la ventana ya agotada NO se imprime una fecha futura. Se imprime lo que sí
 * se puede calcular: cuándo se levantó el precio base, hace cuánto, y cuánto se
 * desfasó desde entonces a la misma deriva que fijó la ventana. Es el caso de
 * 10 de las 23 bandas con fecha —papa y jitomate saladette con 8 días de
 * ventana contra 65 de rezago— y ninguna cifra se teclea: todas salen de la
 * banda y de `lastObservation`.
 *
 * `dias` en el desfase acumulado son días de calendario, no meses redondeados:
 * la extrapolación se dice «cerca de» porque es una recta de cinco meses, no una
 * medición.
 */
function prosaVigencia(
  vigencia: Vigencia | null,
  ctx: {
    observacion: string
    diasDeRezago: number | null
    diasRestantes: number | null
    desfaseAcumulado: number | null
  },
): ProsaVigencia {
  if (vigencia === null) {
    return {
      valor: 'revise cada mes',
      nota: 'Sin tendencia clara: la deriva de esta serie no alcanza el ajuste mínimo para extrapolar una fecha.',
      acta: 'sin tendencia clara — revise cada mes',
    }
  }

  const levantado = formatDate(ctx.observacion)
  const ventana = `${integer(vigencia.dias)} ${pluralize(vigencia.dias, 'día', 'días')}`
  const desfase = percent(VIGENCIA_DESFASE * 100, 0)

  if (ctx.diasRestantes !== null && ctx.diasRestantes < 0) {
    const antiguedad =
      ctx.diasDeRezago === null
        ? ''
        : `, hace ${integer(ctx.diasDeRezago)} ${pluralize(ctx.diasDeRezago, 'día', 'días')}`
    const acumulado =
      ctx.desfaseAcumulado === null
        ? 'y no se puede estimar cuánto se movió desde entonces'
        : `y a esta deriva ya se desfasó cerca de ${percentDelta(ctx.desfaseAcumulado, 0)}`
    /* Con el tope activo la ventana no la cerró el 5 %, la cerró la pantalla:
       decirlo evita que el desfase acumulado —−3 % en el frijol— se lea como si
       el umbral no se hubiera alcanzado y la fecha sobrara. */
    const tope = vigencia.acotado ? ' (el tope de esta pantalla)' : ''
    return {
      valor: `venció el ${formatDate(vigencia.hasta)}`,
      nota: `El precio base se levantó el ${levantado}${antiguedad} ${acumulado}: la ventana era de ${ventana}${tope}. No lo use sin recotizar.`,
      acta: `VENCIDA el ${formatDate(vigencia.hasta)}. La ventana de ${ventana}${tope} corrió desde la última observación, del ${levantado}${antiguedad}, ${acumulado}. No lo use sin recotizar.`,
    }
  }

  /* El día en que la ventana se agota no «quedan 0 días»: se agota hoy, y así se
     dice. El cero aquí es una cuenta real, no un dato ausente, pero leído como
     saldo daría a entender que todavía alcanza. */
  const restan =
    ctx.diasRestantes === null
      ? ''
      : ctx.diasRestantes === 0
        ? ': se agota hoy'
        : `: ${pluralize(ctx.diasRestantes, 'queda', 'quedan')} ${integer(ctx.diasRestantes)} ${pluralize(ctx.diasRestantes, 'día', 'días')}`
  /* `diasSinTope === null` es exactamente la serie plana: es la única rama de
     `vigenciaHasta` que no calcula una fecha de desfase, porque a deriva cero no
     existe. Decirlo así —y no «en mucho más tiempo»— es lo que distingue al
     azúcar, que no se movió un centavo en cinco meses, de un producto cuya
     ventana simplemente cayó más allá del tope. */
  const ritmo = vigencia.acotado
    ? vigencia.diasSinTope === null
      ? `La serie no se movió, así que no hay fecha en que el precio base se desfase ${desfase}; el tope de esta pantalla son ${integer(VIGENCIA_TOPE_DIAS)} días`
      : `A este ritmo el precio base se desfasa ${desfase} en ${integer(vigencia.diasSinTope)} días; el tope de esta pantalla son ${integer(VIGENCIA_TOPE_DIAS)} días`
    : `A este ritmo el precio base se desfasa ${desfase} en ${ventana}`
  return {
    valor: `revisar antes del ${formatDate(vigencia.hasta)}`,
    nota: `${ritmo}, contados desde la última observación, del ${levantado}${restan}.`,
    acta: `revisar antes del ${formatDate(vigencia.hasta)} (${ventana} contados desde la última observación, del ${levantado}${restan})`,
  }
}

/* --- Archivo de detalle -------------------------------------------------- */

/**
 * Una celda del archivo de detalle.
 *
 * No es un `PriceCell`: le faltan `ciLo` y `ciHi` porque `RETAIL_FIELDS` no los
 * declara. El tipo lo dice para que nadie la pase a `PriceBand` por descuido.
 */
interface CeldaDetalle {
  refId: string
  channel: RetailChannel
  geography: string
  period: Period
  n: number
  states: number
  outlets: number
  p10: number
  p25: number
  p50: number
  p75: number
  p90: number
}

interface IndiceDetalle {
  /** Celda exacta en los cuatro ejes, o `null`. Sin respaldo a otro mes. */
  celda(refId: string, canal: RetailChannel, geo: string, period: Period): CeldaDetalle | null
  /** Celdas por entidad —nunca la nacional— de una referencia, canal y mes. */
  entidades(refId: string, canal: RetailChannel, period: Period): CeldaDetalle[]
  /**
   * Geografías con celda para esa referencia, canal y MES, sin `MX`.
   *
   * El mes va en la firma y no es un detalle: sin él, «Entrega en» juntaba las
   * geografías de los cinco meses y ofrecía cortes que el archivo no tiene.
   * Medido sobre este dataset: 459 de las 1 325 combinaciones entidad×mes que
   * el selector alcanzaba —el 34.6 %, en 53 de las 57 referencias con
   * desglose— se quedaban sin celda que enseñar.
   */
  geografias(refId: string, canal: RetailChannel, period: Period): string[]
  /**
   * El `n` más chico de todo el archivo: el piso por debajo del cual el
   * pipeline no escribe celda (guarda G10). Se mide en vez de declararse
   * porque es la razón que la pantalla da cuando un corte no tiene cuantiles,
   * y un umbral tecleado aquí se despegaría del que corre el pipeline. Hoy
   * vale 5 sobre las 9 941 filas del archivo.
   */
  piso: number | null
}

type EstadoCarga = 'inicial' | 'cargando' | 'listo' | 'error'

const CANALES = Object.keys(RETAIL_CHANNEL_LABEL) as RetailChannel[]

/**
 * Índice del archivo de detalle, construido una sola vez.
 *
 * Las filas vienen como ARREGLOS con `fields` declarando el orden: con casi
 * diez mil filas, repetir doce llaves cuesta más que el dato. Las columnas se
 * leen por NOMBRE y nunca por posición, porque el orden de `RETAIL_FIELDS` es un
 * contrato del pipeline: si una columna se va, el archivo se rechaza entero y la
 * vista lo declara. Rellenar la faltante publicaría cuantiles corridos una
 * posición, que es el error que no se ve.
 */
function construirIndice(bruto: unknown): IndiceDetalle | null {
  if (typeof bruto !== 'object' || bruto === null) return null
  const payload = bruto as { fields?: unknown; rows?: unknown }
  const fields = payload.fields
  const rows = payload.rows
  if (!Array.isArray(fields) || !Array.isArray(rows)) return null

  const col = (nombre: string): number => fields.indexOf(nombre)
  const iRef = col('refId')
  const iCanal = col('canal')
  const iGeo = col('geo')
  const iPeriodo = col('periodo')
  const numericas = ['n', 'states', 'outlets', 'p10', 'p25', 'p50', 'p75', 'p90'].map(col)
  if ([iRef, iCanal, iGeo, iPeriodo, ...numericas].some((i) => i < 0)) return null

  const porLlave = new Map<string, CeldaDetalle>()
  const porEntidad = new Map<string, CeldaDetalle[]>()
  const porGeografia = new Map<string, Set<string>>()
  let piso: number | null = null

  for (const fila of rows) {
    if (!Array.isArray(fila)) continue
    const refId = fila[iRef]
    const canal = fila[iCanal]
    const geo = fila[iGeo]
    const periodo = fila[iPeriodo]
    if (typeof refId !== 'string' || typeof canal !== 'string') continue
    if (typeof geo !== 'string' || typeof periodo !== 'string') continue
    /* Un canal que no está en el catálogo no se castea a la fuerza: se salta.
       Un `as RetailChannel` sobre texto desconocido metería una llave que
       `RETAIL_CHANNEL_LABEL` no tiene y la vista imprimiría `undefined`. */
    if (!CANALES.includes(canal as RetailChannel)) continue

    const valores = numericas.map((i) => fila[i])
    if (valores.some((v) => typeof v !== 'number' || !Number.isFinite(v))) continue
    const [n, states, outlets, p10, p25, p50, p75, p90] = valores as number[]
    if (
      n === undefined ||
      states === undefined ||
      outlets === undefined ||
      p10 === undefined ||
      p25 === undefined ||
      p50 === undefined ||
      p75 === undefined ||
      p90 === undefined
    ) {
      continue
    }

    const celda: CeldaDetalle = {
      refId,
      channel: canal as RetailChannel,
      geography: geo,
      period: periodo,
      n,
      states,
      outlets,
      p10,
      p25,
      p50,
      p75,
      p90,
    }
    porLlave.set(`${refId}|${canal}|${geo}|${periodo}`, celda)
    /* El piso se mide sobre TODAS las filas, nacionales incluidas: la guarda
       del pipeline es una sola y no distingue geografía. */
    if (piso === null || n < piso) piso = n
    if (geo !== NACIONAL) {
      const llave = `${refId}|${canal}|${periodo}`
      const lista = porEntidad.get(llave)
      if (lista === undefined) porEntidad.set(llave, [celda])
      else lista.push(celda)

      const llaveGeo = `${refId}|${canal}|${periodo}`
      const set = porGeografia.get(llaveGeo)
      if (set === undefined) porGeografia.set(llaveGeo, new Set([geo]))
      else set.add(geo)
    }
  }

  return {
    celda: (refId, canal, geo, period) => porLlave.get(`${refId}|${canal}|${geo}|${period}`) ?? null,
    entidades: (refId, canal, period) => porEntidad.get(`${refId}|${canal}|${period}`) ?? [],
    geografias: (refId, canal, period) =>
      [...(porGeografia.get(`${refId}|${canal}|${period}`) ?? [])].sort(),
    piso,
  }
}

/**
 * Carga diferida del archivo de detalle.
 *
 * Calca `AbastoDirectorio.tsx:98-120`, incluido el `useRef` que ataja el doble
 * efecto de StrictMode —sin él la descarga se dispara dos veces en desarrollo—
 * y `import.meta.env.BASE_URL`, porque el sitio puede vivir en un subdirectorio
 * y una ruta absoluta lo rompería ahí.
 */
function useDetalle(archivo: string): { indice: IndiceDetalle | null; estado: EstadoCarga } {
  const [indice, setIndice] = useState<IndiceDetalle | null>(null)
  const [estado, setEstado] = useState<EstadoCarga>('inicial')
  const pedido = useRef(false)

  useEffect(() => {
    if (archivo === '' || pedido.current) return
    pedido.current = true
    setEstado('cargando')
    fetch(`${import.meta.env.BASE_URL}${archivo}`)
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`)
        return r.json() as Promise<unknown>
      })
      .then((bruto) => {
        const construido = construirIndice(bruto)
        if (construido === null) throw new Error('El archivo de detalle cambió de formato')
        setIndice(construido)
        setEstado('listo')
      })
      .catch(() => setEstado('error'))
  }, [archivo])

  return { indice, estado }
}

/* --- Casilla de texto ---------------------------------------------------- */

/**
 * Casilla con la silueta de `StatTile` para un valor que NO es un número.
 *
 * Dos de las cuatro casillas de §4.4 valen texto: «—» cuando el renglón no
 * tiene margen contra el anaquel —le pasa a la mayoría de las presentaciones— y
 * «venció el 6 de junio de 2026» en la vigencia. `StatTile` recibe
 * `value: number` y lo anima con resorte, así que la única forma de meterlas
 * ahí sería un `?? 0`, y un cero pintaría «0.0 %» donde lo que hay es una
 * ausencia. La casilla reusa las clases de `statTile.css` —que entra con el
 * `import` de `StatTile`, arriba, y por eso está siempre disponible— y respeta
 * el mismo contrato: etiqueta · valor · nota.
 */
function Casilla({
  label,
  valor,
  nota,
  trend,
}: {
  label: string
  valor: string
  nota: string
  trend?: number[]
}) {
  return (
    <div className="stat">
      <p className="stat__label t-footnote ink-2">{label}</p>
      <div className="stat__row">
        <p className="stat__value pb-casilla__valor">{valor}</p>
        {trend && trend.length > 2 ? <Sparkline values={trend} /> : null}
      </div>
      <p className="stat__note t-caption ink-3">{nota}</p>
    </div>
  )
}

/* --- Filtros ------------------------------------------------------------- */

/**
 * El estado que la URL persiste (`?renglon=&pres=&entrega=&mes=`).
 *
 * La vista se controla desde fuera para que `App.tsx` pueda escribirlo en el
 * hash omitiendo los valores por omisión, que es la regla de la casa: la URL
 * limpia tiene que seguir siendo `#/precio-base`. Mientras la ruta no exista,
 * los cuatro campos llegan vacíos y la vista usa los de omisión, así que
 * funciona igual montada suelta.
 */
export interface PrecioBaseFiltros {
  /** Clave ENIGH del renglón. */
  renglon: string | null
  /** `refId` de la presentación de referencia. */
  pres: string | null
  /** `MX` o clave de entidad de dos dígitos. */
  entrega: string | null
  mes: Period | null
}

/**
 * Los valores por omisión, derivados del dataset.
 *
 * `App.tsx` los necesita para omitirlos al escribir el hash. No se teclea
 * ninguno: el renglón sale del primer grupo declarado, la presentación es la
 * PRIMERA que el pipeline emite para ese renglón —y no la de más observaciones
 * del último mes, que es una regla parecida y equivocada: R2 ordena por n
 * acumulada en las diez quincenas, y con la n de mayo el frijol elegiría el
 * pinto (63) sobre el negro (62), que es la presentación declarada y anclada en
 * `verify`— y el mes es el último de la fuente.
 */
export function precioBaseDefaults(retail: RetailComparator | null): PrecioBaseFiltros {
  if (retail === null) return { renglon: null, pres: null, entrega: null, mes: null }
  const claves = [...new Set(retail.references.map((r) => r.claveEnigh))]
  const renglon = RENGLONES_POR_TONELADA.find((c) => claves.includes(c)) ?? claves[0] ?? null
  const pres = retail.references.find((r) => r.claveEnigh === renglon)?.id ?? null
  const mes = retail.periods[retail.periods.length - 1] ?? null
  return { renglon, pres, entrega: NACIONAL, mes }
}

export interface PrecioBaseProps {
  filtros?: PrecioBaseFiltros
  onFiltros?: (filtros: PrecioBaseFiltros) => void
}

/* --- Cáscara: la fuente puede no existir (§4.11) ------------------------- */

export function PrecioBase({ filtros, onFiltros }: PrecioBaseProps) {
  const retail = dataset.retail
  if (retail === null) {
    return (
      <>
        <Cabecera />
        <Callout kind="warning" title="Los precios de tienda no se pudieron ingerir">
          <p>
            La última corrida del pipeline no logró leer los datos abiertos de Profeco, así que esta
            pantalla no tiene qué mostrar. El resto del sitio no depende de esta fuente: el costo de
            la canasta, el desglose por producto y el índice territorial siguen siendo los del
            INEGI.
          </p>
        </Callout>
      </>
    )
  }
  return <Vista retail={retail} filtros={filtros} onFiltros={onFiltros} />
}

function Cabecera({ descripcion = false }: { descripcion?: boolean }) {
  return (
    <header className="view-head">
      <p className="t-micro view-head__eyebrow">Precio base</p>
      <h1 className="t-title1">A qué precio convocar una compra</h1>
      {descripcion ? (
        <p className="t-body ink-2">
          Precio observado en central de abasto para el producto y la presentación exactos que se
          van a licitar, con el rango en que se negocia, en cuántas entidades se midió y qué tan
          rápido se está moviendo.
        </p>
      ) : null}
    </header>
  )
}

/* --- La vista ------------------------------------------------------------ */

function Vista({
  retail,
  filtros,
  onFiltros,
}: {
  retail: RetailComparator
} & PrecioBaseProps) {
  const omision = useMemo(() => precioBaseDefaults(retail), [retail])
  const { indice, estado: estadoDetalle } = useDetalle(retail.detailFile)

  /* Estado local con eco del de la URL: mientras `App.tsx` no enrute la vista,
     `filtros` llega vacío y manda el local; cuando enrute, el de fuera gana en
     cuanto cambia —botón Atrás incluido— y el local lo sigue. */
  const [local, setLocal] = useState<PrecioBaseFiltros>(() => filtros ?? omision)
  useEffect(() => {
    if (filtros !== undefined) setLocal(filtros)
  }, [filtros])

  const [oferta, setOferta] = useState('')
  /* El bulto de la compra: toneladas si el precio va por kilo, litros si va por
     litro. Un solo estado porque es un solo campo; lo que cambia es el rótulo. */
  const [bulto, setBulto] = useState('')
  const [mesScrub, setMesScrub] = useState<Period | null>(null)

  const nombreEntidad = useMemo(() => {
    const mapa = new Map<string, string>()
    for (const place of dataset.places) {
      if (place.kind === 'entidad' && place.stateCode !== null) mapa.set(place.stateCode, place.name)
    }
    return mapa
  }, [])

  /* --- Resolución de los filtros contra el dato ------------------------- */

  const claves = useMemo(
    () => [...new Set(retail.references.map((r) => r.claveEnigh))],
    [retail],
  )
  const renglon =
    local.renglon !== null && claves.includes(local.renglon)
      ? local.renglon
      : (omision.renglon ?? claves[0] ?? '')

  const presentaciones = retail.references.filter((r) => r.claveEnigh === renglon)
  const referencia = presentaciones.find((r) => r.id === local.pres) ?? presentaciones[0] ?? null
  const refId = referencia?.id ?? ''

  const periodos = retail.periods.slice(-12)
  const mes =
    local.mes !== null && periodos.includes(local.mes)
      ? local.mes
      : (periodos[periodos.length - 1] ?? '')
  const mesReferencia = retail.periods[retail.periods.length - 1] ?? ''

  /* «Entrega en» ofrece las entidades con celda EN EL MES ELEGIDO, no en
     cualquiera de los meses del archivo. Juntarlas todas ofrecía cortes que no
     existen —frijol negro en Guanajuato existe en enero y no en mayo, y mayo es
     el mes por omisión—: 459 de las 1 325 combinaciones entidad×mes alcanzables
     desde el selector no tenían celda, y la pantalla se quedaba prometiendo
     cuantiles que nunca iban a llegar. Al acotarlo, el selector ofrece 1 166
     cortes en vez de 1 625 y todos tienen dato. */
  const entidadesConCentral = indice?.geografias(refId, 'central', mes) ?? []
  const entrega =
    local.entrega !== null &&
    (local.entrega === NACIONAL || entidadesConCentral.includes(local.entrega))
      ? local.entrega
      : NACIONAL
  const esNacional = entrega === NACIONAL
  const rotuloEntrega = esNacional ? 'Nacional' : (nombreEntidad.get(entrega) ?? entrega)

  const producto = dataset.products.find(
    (p) => referencia !== null && p.id === `enigh-${referencia.claveEnigh.toLowerCase()}`,
  )
  const nombreRenglon = producto?.name ?? referencia?.label ?? renglon

  /* --- El corte -------------------------------------------------------- */

  const banda: PriceBandData | null = retailBand(retail, refId, entrega, mes)
  const celdaDetalle = indice?.celda(refId, 'central', entrega, mes) ?? null
  const celdaPublicada: PriceCell | null = banda?.central ?? null
  const publicable = banda !== null && banda.grade === 'publicable' && banda.central !== null

  const serieInegi: SeriesPoint[] | null = useMemo(() => {
    if (producto === undefined) return null
    /* Ámbito urbano y no rural: QQP levanta en 74 municipios urbanos y el precio
       urbano del boletín es el comparable. La vista no recibe ámbito, y elegir
       el rural aquí cruzaría dos universos distintos sin decirlo. */
    const serie = dataset.productSeries.find(
      (s) => s.productId === producto.id && s.kind === 'precio' && s.geography === 'urbano',
    )
    return serie?.points ?? null
  }, [producto])

  const marcas = useMemo(() => retailMarkers(banda, serieInegi), [banda, serieInegi])

  /* --- 4.1 El contraste contra el INEGI --------------------------------- */

  const contraste = retail.contrasteInegi
  /* Las tres cifras de la tortilla se derivan; si falta una, la frase entera no
     se imprime. Media frase con un número inventado es peor que ninguna. */
  const tortilla = useMemo(() => {
    const ref = retail.references.find((r) => r.claveEnigh === 'A004')
    if (ref === undefined) return null
    const bandaTortilla = retailBand(retail, ref.id, NACIONAL, mesReferencia)
    const enTortilleria = indice?.celda(ref.id, 'tortilleria', NACIONAL, mesReferencia) ?? null
    const enAnaquel = bandaTortilla?.anaquel ?? null
    const boletin = dataset.productSeries
      .find((s) => s.productId === 'enigh-a004' && s.kind === 'precio' && s.geography === 'urbano')
      ?.points.find((p) => p.period === contraste.period)
    if (enTortilleria === null || enAnaquel === null || boletin === undefined) return null
    return {
      tortilleria: enTortilleria.p50,
      anaquel: enAnaquel.p50,
      boletin: boletin.value,
      canalAnaquel: RETAIL_CHANNEL_LABEL[enAnaquel.channel].toLowerCase(),
    }
  }, [retail, indice, mesReferencia, contraste.period])

  /* --- 4.4 Las cuatro casillas ------------------------------------------ */

  const serieCentral = retail.series.find((s) => s.refId === refId && s.channel === 'central')
  const puntosCentral = serieCentral?.points ?? []
  const tendencia = puntosCentral.map((p) => p.value)
  const primerMesSerie = puntosCentral[0]?.period ?? null

  /* La deriva y la vigencia describen la SERIE mensual nacional en central, no
     el corte elegido: `retail.series` no trae desglose por entidad y la gráfica
     de §4.8 lo dice en su subtítulo. Leerlas de `banda` las apagaba en cuanto el
     lector elegía una entidad —o un mes que no fuera el de referencia—, y las
     dos casillas caían al texto que R12 reserva para R² < 0.5. Un banco de CDMX
     que filtre a su entidad, que es lo natural, recibía «revise cada mes» sobre
     una papa que en el corte nacional se desfasa 5 % en 8 días. */
  const bandaNacional = retailBand(retail, refId, NACIONAL, mesReferencia)
  /* `banda !== null` significa exactamente «el corte elegido es el que el
     pipeline publica»: `retail.bands` sólo trae el nacional del mes de
     referencia. Es lo que decide si las casillas tienen que rotular su base. */
  const esCortePublicado = banda !== null
  const deriva = bandaNacional?.derivaMensualPct ?? null
  const derivaR2 = bandaNacional?.derivaR2 ?? null
  /* La serie que no se movió un centavo. Va con la serie en la mano y no con el
     R², porque en las cuatro referencias planas ese R² es 0/0. */
  const estable = serieEstable(deriva, puntosCentral)
  /* Dos fechas distintas y ninguna sustituye a la otra: la ficha se emite HOY y
     la vigencia corre desde la ÚLTIMA OBSERVACIÓN, que es sobre lo que se
     estimó la recta. Contarla desde hoy regalaba los días de rezago de la
     fuente —la papa imprimía «revisar antes del 10 de agosto» cuando su ventana
     de 8 días se agotó el 6 de junio— y con ella convoca un comprador. */
  const hoyIso = hoyIsoLocal()
  const vigencia = vigenciaHasta(deriva, derivaR2, retail.lastObservation, estable !== null)
  /* Días de calendario contados hoy, no `retail.ageDays`: ese campo lo congela
     el pipeline con el reloj de la corrida —el dataset trae 64, de la corrida
     del 1 de agosto— y esta pantalla se abre después. §4.0 pide los días
     «calculados de `lastObservation`», que es exactamente esto. */
  const diasDeRezago = diasEntreIso(retail.lastObservation, hoyIso)
  const diasRestantes = vigencia === null ? null : diasEntreIso(hoyIso, vigencia.hasta)
  const vigenciaVencida = diasRestantes !== null && diasRestantes < 0
  /* Lo único que se puede afirmar cuando la ventana ya se agotó, y sale de la
     misma recta que la fijó. Sin ventana vencida no se calcula: dentro de la
     vigencia el desfase acumulado es justamente lo que todavía no importa. */
  const desfaseAcumulado = vigenciaVencida ? derivaAcumuladaPct(deriva, diasDeRezago) : null
  const prosaVig = prosaVigencia(vigencia, {
    observacion: retail.lastObservation,
    diasDeRezago,
    diasRestantes,
    desfaseAcumulado,
  })

  const margen = banda?.margin ?? null
  const margenPct = margen?.pct ?? null
  /* El canal contra el que se midió el margen sale de la propia celda de
     anaquel. Sin celda se dice «el anaquel», que es el genérico verdadero, y no
     «autoservicio»: nombrar un canal que no se leyó del dato sería inventar de
     dónde salió la comparación. */
  const canalAnaquel = banda?.anaquel?.channel ?? null
  const rotuloAnaquel =
    canalAnaquel === null ? 'el anaquel' : RETAIL_CHANNEL_LABEL[canalAnaquel].toLowerCase()

  /* «Es el caso de N de las M» se cuenta sobre las bandas de la corrida. Hoy no
     son las 14 que traía el borrador de la especificación, y por eso no se
     teclea. Se cuentan presentaciones, no renglones: cada banda es una
     presentación de referencia y varias comparten clave ENIGH. */
  const conteoVeredictos = useMemo(() => {
    let conNumero = 0
    let sinDiferencia = 0
    for (const b of retail.bands) {
      const v = b.margin.verdict
      if (v === 'anaquel-mas-caro' || v === 'central-mas-cara' || v === 'sin-diferencia') conNumero++
      if (v === 'sin-diferencia') sinDiferencia++
    }
    return { conNumero, sinDiferencia }
  }, [retail])

  /* --- 4.5 El evaluador -------------------------------------------------- */

  const ofertaNum = parsearNumero(oferta)
  const bultoNum = parsearNumero(bulto)
  /* La celda entra sólo si la banda pasó la compuerta: con `solo-rango` llega
     `null` y el evaluador no se puede habilitar por olvido. */
  const celdaCalificable = publicable && banda !== null ? banda.central : null
  /* El denominador viaja hasta `scoreOffer` para que el total no salga de
     multiplicar litros por los 1 000 kilos de una tonelada, que es la densidad
     de 1 kg/L que R11 prohíbe. Son nueve referencias en `L`: aceite, leche,
     agua y refresco. */
  const calificacion =
    ofertaNum === null || referencia === null
      ? null
      : scoreOffer(celdaCalificable, ofertaNum, bultoNum, referencia.denominador)

  /* --- 4.6 Dónde se levantó ese precio ---------------------------------- */

  const anclaNacional = indice?.celda(refId, 'central', NACIONAL, mes) ?? null
  /* Entidades con al menos una observación en el mes, según la celda nacional
     del mismo archivo. Es el denominador honesto de esta lista: lo que falta
     entre ésta y `porEntidad.length` no son entidades sin dato, son entidades
     cuyo dato no llegó al piso del pipeline. Medido sobre los 299 cortes
     referencia×mes del archivo: en 190 se publica menos de la mitad, y en 47 no
     se publica ninguna. */
  const entidadesConObservacion = anclaNacional?.states ?? null
  const porEntidad = useMemo<BarRow[]>(() => {
    const ancla =
      anclaNacional === null ? null : { ...anclaNacional, ciLo: null, ciHi: null }
    /* Sin filtro por `n`: el archivo ya viene filtrado por el pipeline y
       cualquier umbral de aquí sólo puede mentir sobre celdas que no existen. */
    return (indice?.entidades(refId, 'central', mes) ?? [])
      .map((c) => {
        const desviacion = desviacionGeografica({ ...c, ciLo: null, ciHi: null }, ancla)
        return {
          id: c.geography,
          label: nombreEntidad.get(c.geography) ?? c.geography,
          sublabel: `${integer(c.n)} ${pluralize(c.n, 'observación', 'observaciones')} en ${integer(c.outlets)} ${pluralize(c.outlets, 'punto de venta', 'puntos de venta')}`,
          value: c.p50,
          magnitude: desviacion === null ? null : desviacion.pct,
        }
      })
      .sort((a, b) => a.value - b.value)
  }, [indice, refId, mes, anclaNacional, nombreEntidad])

  const dispersion = useMemo(() => {
    if (porEntidad.length < 2) return null
    const barata = porEntidad[0]
    const cara = porEntidad[porEntidad.length - 1]
    if (barata === undefined || cara === undefined || barata.value <= 0) return null
    return { pct: (cara.value / barata.value - 1) * 100, barata, cara }
  }, [porEntidad])

  /* --- 4.7 Contra el anaquel -------------------------------------------- */

  const ranking = retail.rankings.find((r) => r.refId === refId && r.period === mes) ?? null
  const traslape = chainRankingCommonStates(ranking)
  /* Sin ranking no hay cadenas: el cero es el conteo verdadero, no un relleno
     de una cifra ausente. El pipeline emite ranking para la mitad de las
     referencias, así que la rama sin ranking no es un caso de borde. */
  const cadenasEnRanking = ranking === null ? 0 : ranking.chains.length

  /* El ancla sale del propio ranking y NO de `celdaPublicada`, que es la
     mediana agrupada de la central sobre sus 11 entidades mientras las cadenas
     salen de la mediana de medianas sobre las 16-19 comunes: dos bases
     distintas, y ponerlas una junto a otra no es una comparación. Medido en
     mayo de 2026 sobre los 21 rankings que esta sección pinta, el ancla se
     mueve entre −30.0 % y +25.0 % al medirla con la regla de las cadenas, y en
     6 de los 21 eso cambia de lado a alguna cadena: 9 de las 85 filas. La papa
     pasa de $38.00 a $39.50 y Chedraui, que empataba el ancla, queda debajo. */
  const anclaRanking = ranking?.central ?? null

  const filasRanking = useMemo<BarRow[]>(() => {
    if (ranking === null) return []
    /* La barra mide la DISTANCIA contra la central, no el precio otra vez.
       Con `c.p50` en las dos columnas, `BarList` enciende el rótulo visible de
       celda —el que sólo tiene sentido cuando valor y magnitud comparten unidad
       (`BarList.tsx:110-113`, `barList.css:210-213`)— y bajo 640 px cada
       renglón imprimía «Precio: $26.00» y debajo «Comparación: $26.00»: un
       encabezado que promete una segunda cifra y repite la primera, seis veces
       por tabla y dos veces por cadena en el lector de pantalla.

       Sin ancla no hay contra qué medir y la barra se calla; `BarList` lo dice
       fila por fila. */
    const contraLaCentral = (p50: number): number | null =>
      anclaRanking === null || anclaRanking.p50 <= 0 ? null : (p50 / anclaRanking.p50 - 1) * 100

    const filas: RetailRow[] = [
      /* Sin cotización de la central en ninguna entidad común no hay fila
         ancla: inventarla con el nacional es exactamente lo que se corrigió. */
      ...(anclaRanking === null
        ? []
        : [
            {
              id: 'central',
              label: RETAIL_CHANNEL_LABEL.central,
              /* La base va completa porque NO es la de las cadenas: la central
                 existe en 11 de las 32 entidades y casi nunca cotiza en todas
                 las comunes —hoy entre 1 y 7 de ellas—, así que el número de
                 medianas que sostienen esta fila es parte de la cifra. */
              sublabel: `ancla del ranking · mediana de ${integer(anclaRanking.n)} ${pluralize(anclaRanking.n, 'mediana estatal', 'medianas estatales')}, de las ${integer(anclaRanking.states)} comunes`,
              value: anclaRanking.p50,
              /* Cero medido, no relleno: es la fila contra la que se leen las
                 demás y su distancia contra sí misma es exactamente ninguna. */
              magnitude: contraLaCentral(anclaRanking.p50),
              ancla: true,
            },
          ]),
      ...ranking.chains.map((c) => ({
        id: c.chainId,
        label: rotuloCadena(c.chainId),
        /* La misma base que declara la fila ancla, y por la misma razón. `n` NO
           es el número de precios levantados: es cuántas medianas estatales
           entraron a la mediana de medianas de R9 —por eso vale lo mismo que
           `states` en las 121 filas de cadena del dataset—. Llamarlo
           «cotizaciones» hacía leer 16 precios sueltos, una muestra que
           cualquiera descartaría por delgada, donde hay 16 medianas construidas
           cada una sobre muchas observaciones. El conteo crudo no se publica;
           hasta que se publique, esta fila no puede prometerlo. */
        sublabel: `mediana de ${integer(c.n)} ${pluralize(c.n, 'mediana estatal', 'medianas estatales')}`,
        value: c.p50,
        magnitude: contraLaCentral(c.p50),
      })),
    ]
    /* `sortRetailRows` ordena por MAGNITUD y manda al final lo que no la tiene.
       Con ancla eso sigue siendo ordenar por precio —la distancia contra la
       central crece con el precio—; sin ancla todas las magnitudes son `null` y
       el desempate caería en el alfabético. Un ranking de precios ordenado por
       nombre no es un ranking, así que ahí se ordena con el precio y la barra se
       apaga al mapear. */
    const ordenadas = sortRetailRows(
      anclaRanking === null ? filas.map((f) => ({ ...f, magnitude: f.value })) : filas,
      'barato',
    )
    return ordenadas.map((f) => ({
      id: f.id,
      label: f.label,
      sublabel: f.sublabel,
      value: f.value,
      magnitude: anclaRanking === null ? null : f.magnitude,
    }))
  }, [ranking, anclaRanking])

  /* Presentación hermana del mismo renglón que SÍ tiene número: es la salida
     concreta que ofrece el microcopy de `sin-paridad-de-canal`, y por eso se
     busca en el dato en vez de nombrarla en la prosa —el borrador de la
     especificación nombraba «la bolsa de 900 g» y esa presentación ya no está
     en el catálogo—. */
  const alternaConMargen =
    presentaciones.find((r) => {
      if (r.id === refId) return false
      const b = retailBand(retail, r.id, NACIONAL, mesReferencia)
      return b !== null && b.margin.pct !== null
    }) ?? null

  /* Cuántas presentaciones del grupo de tonelada comparten el veredicto, y
     cuántas son en total: es la línea en cursivas de §4.7 y las dos cifras
     salen del dato. El borrador de la especificación decía «los tres productos
     que BAMX compra por tonelada» y hoy son más presentaciones que renglones,
     así que ninguna de las dos se escribe. */
  const tonelada = useMemo(() => {
    const refsGrupo = retail.references.filter((r) =>
      RENGLONES_POR_TONELADA.includes(r.claveEnigh),
    )
    const ids = new Set(refsGrupo.map((r) => r.id))
    const sinCanal = retail.bands.filter(
      (b) => b.margin.verdict === 'sin-paridad-de-canal' && ids.has(b.refId),
    ).length
    return { total: refsGrupo.length, sinCanal }
  }, [retail])

  /* --- 4.8 Cómo se ha movido -------------------------------------------- */

  const seriesGrafica = useMemo<ChartSeries[]>(() => {
    const out: ChartSeries[] = []
    for (const { channel, color } of CANAL_SERIE) {
      const s = retail.series.find((x) => x.refId === refId && x.channel === channel)
      if (s === undefined || s.points.length < 2) continue
      out.push({
        id: `${refId}-${channel}`,
        label: RETAIL_CHANNEL_LABEL[channel],
        shortLabel: RETAIL_CHANNEL_LABEL[channel],
        color,
        points: s.points.map((p) => ({ period: p.period, value: p.value })),
      })
    }
    return out
  }, [retail, refId])

  /* Los meses sin observaciones suficientes no se dibujan ni se interpolan: se
     anotan. Un hueco sin marca se lee como una línea continua que sí existió. */
  const huecos = useMemo(() => {
    const conPunto = new Set(seriesGrafica.flatMap((s) => s.points.map((p) => p.period)))
    return retail.periods
      .filter((p) => !conPunto.has(p))
      .map((p) => ({ period: p, label: 'sin observaciones suficientes' }))
  }, [seriesGrafica, retail.periods])

  const reglas =
    publicable && banda !== null && banda.central !== null
      ? [{ value: banda.central.p50, label: 'precio base' }]
      : []

  const valoresScrub = useMemo(() => {
    if (mesScrub === null) return []
    return seriesGrafica
      .map((s) => {
        const punto = s.points.find((p) => p.period === mesScrub)
        return punto === undefined ? null : `${s.label} ${currency(punto.value)}`
      })
      .filter((v): v is string => v !== null)
  }, [mesScrub, seriesGrafica])

  /* --- 4.9 La ficha ------------------------------------------------------ */

  const fuente = dataset.sources.find((s) => s.id === retail.sourceId) ?? null

  const ficha = useMemo<[string, string][]>(() => {
    if (banda === null || celdaPublicada === null || referencia === null) return []
    const c = celdaPublicada
    const quincenasDelMes = retail.cobertura.quincenas.filter((q) => {
      const m = /^(\d{2})-(\d{4})_/.exec(q)
      return m !== null && `${m[2]}-${m[1]}` === mes
    })
    const dispersionRel = c.p50 > 0 ? ((c.p75 - c.p25) / c.p50) * 100 : null
    const entidadesDelPais = dataset.places.filter((p) => p.kind === 'entidad').length

    const filas: [string, string][] = [
      ['Renglón', `${nombreRenglon} (clave ENIGH ${referencia.claveEnigh})`],
      [
        'Presentación',
        `${referencia.label} («${referencia.presentacionQqp}» en el catálogo de Profeco)`,
      ],
      ['Canal', RETAIL_CHANNEL_LABEL.central],
      [
        'Ámbito',
        `${rotuloEntrega} — ${integer(c.states)} ${pluralize(c.states, 'entidad con observación', 'entidades con observación')}`,
      ],
      [
        'Periodo',
        quincenasDelMes.length > 0
          ? `${capitalizar(formatPeriodLong(mes))} (quincenas ${quincenasDelMes.join(' y ')})`
          : capitalizar(formatPeriodLong(mes)),
      ],
      [
        'Precio base',
        banda.grade === 'publicable'
          ? `${currency(c.p50)} ${porUnidad(referencia)}`
          : `NO PUBLICADO — ${banda.gradeReason ?? 'la celda no pasó la compuerta de publicación'}`,
      ],
      ['Rango de negociación', `${currency(c.p25)} – ${currency(c.p75)} (p25 – p75)`],
      [
        'Intervalo de confianza',
        c.ciLo !== null && c.ciHi !== null
          ? `${currency(c.ciLo)} – ${currency(c.ciHi)} (95 %, estadísticos de orden)`
          : `no calculable con ${integer(c.n)} ${pluralize(c.n, 'observación', 'observaciones')}`,
      ],
      [
        'Observaciones',
        `${integer(c.n)}, en ${integer(c.outlets)} ${pluralize(c.outlets, 'punto de venta', 'puntos de venta')}`,
      ],
      [
        'Dispersión relativa',
        dispersionRel === null ? 'no calculable' : `${percent(dispersionRel, 0)} del precio base`,
      ],
      /* La serie plana va primero, antes de mirar el R², por lo mismo que en la
         casilla: el 0.00 del dataset es 0/0 y meterlo al acta como «ajuste» le
         pondría al precio más estable la advertencia más severa. */
      [
        'Deriva',
        estable !== null
          ? `sin cambio: ${currency(estable.valor)} ${porUnidad(referencia)} los ${integer(estable.meses)} meses de la serie, de ${formatPeriodLong(estable.desde)} a ${formatPeriodLong(estable.hasta)}. El R² de la recta sale de dividir cero entre cero y no mide ajuste`
          : deriva === null || derivaR2 === null
            ? 'sin serie suficiente para estimarla'
            : derivaR2 < DERIVA_R2_MINIMO
              ? `${percentDelta(deriva)} al mes con R² ${number(derivaR2, 2)} — sin tendencia clara: por debajo de ${number(DERIVA_R2_MINIMO, 1)} la deriva no se presenta como tendencia`
              : `${percentDelta(deriva)} al mes (R² ${number(derivaR2, 2)}) — ${rumboDeriva(deriva, primerMesSerie)}`,
      ],
      /* La ficha lleva la fecha de emisión arriba y ésta es la otra fecha: son
         dos y se leen como dos. La vigencia arranca en la última observación,
         que este renglón nombra, y no en el día en que se imprime el acta. */
      ['Vigencia sugerida', prosaVig.acta],
      ['Diferencial contra anaquel', fraseMargen(banda)],
    ]

    if (fuente !== null) {
      filas.push(['Fuente', `${fuente.institution}, ${fuente.name}`])
      filas.push(['Fuente · enlace', fuente.url])
      if (typeof fuente.license === 'string') filas.push(['Fuente · licencia', fuente.license])
      filas.push([
        'Fuente · consulta',
        `${formatDate(fuente.retrievedAt)}. Última observación en la fuente: ${formatDate(retail.lastObservation)} (${fraseAntiguedad(diasDeRezago)} al emitirse esta ficha).`,
      ])
    }
    filas.push([
      'Límite 1',
      'Es un precio de venta observado en establecimientos, no una cotización de proveedor. No incluye flete, maniobras, empaque ni condiciones de pago.',
    ])
    filas.push([
      'Límite 2',
      `Profeco no publica el diseño de su muestra. Las ${integer(c.n)} observaciones son las que Profeco levantó, no una muestra probabilística de la central.`,
    ])
    filas.push([
      'Límite 3',
      `No es el precio de la canasta alimentaria. Contra el boletín del INEGI de ${formatPeriodLong(contraste.period)}, esta fuente se desvía en mediana ${percent(contraste.desviacionMedianaPct)} por renglón y sólo ${integer(contraste.dentroDe10)} de ${integer(contraste.renglones)} renglones quedan dentro de ±10 %. Las dos cifras miden cosas distintas y no deben restarse ni promediarse.`,
    ])
    filas.push([
      'Límite 4',
      `La central de abasto no existe en todas las entidades: esta presentación se levantó en ${integer(c.states)} de las ${integer(entidadesDelPais)} entidades del país. Un precio base nacional derivado de aquí es indicativo, no regionalizable.`,
    ])
    return filas
  }, [
    banda,
    celdaPublicada,
    referencia,
    retail,
    mes,
    nombreRenglon,
    rotuloEntrega,
    deriva,
    derivaR2,
    estable,
    primerMesSerie,
    prosaVig,
    diasDeRezago,
    fuente,
    contraste,
  ])

  function descargarFicha(): void {
    if (ficha.length === 0) return
    const esc = (v: string): string => (/[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v)
    const filas: string[][] = [['campo', 'valor'], ...ficha.map(([k, v]) => [k, v])]
    /* BOM al inicio: sin él, Excel en Windows abre el CSV en latin-1 y destroza
       cada acento. CRLF por la misma razón: es el CSV que Excel espera. */
    const csv = `\uFEFF${filas.map((r) => r.map(esc).join(',')).join('\r\n')}\r\n`
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }))
    const a = document.createElement('a')
    a.href = url
    a.download = `precio-base-${refId}-${mes}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  /* --- Opciones de los filtros ------------------------------------------ */

  const rotuloRenglon = (clave: string): string =>
    dataset.products.find((p) => p.id === `enigh-${clave.toLowerCase()}`)?.name ?? clave

  const grupoTonelada = RENGLONES_POR_TONELADA.filter((c) => claves.includes(c))
  const renglonOptions = [
    ...grupoTonelada,
    ...claves
      .filter((c) => !grupoTonelada.includes(c))
      .sort((a, b) => rotuloRenglon(a).localeCompare(rotuloRenglon(b), 'es-MX')),
  ].map((c) => ({ value: c, label: rotuloRenglon(c) }))

  const presOptions = presentaciones.map((r) => ({ value: r.id, label: r.label }))

  const entregaOptions = [
    {
      value: NACIONAL,
      label:
        celdaPublicada === null
          ? 'Nacional'
          : `Nacional — ${integer(celdaPublicada.states)} ${pluralize(celdaPublicada.states, 'entidad', 'entidades')}`,
    },
    ...entidadesConCentral.map((geo) => ({
      value: geo,
      label: nombreEntidad.get(geo) ?? geo,
    })),
  ]

  const mesOptions = periodos
    .slice()
    .reverse()
    .map((p) => ({ value: p, label: capitalizar(formatPeriodLong(p)) }))

  function cambiar(parcial: Partial<PrecioBaseFiltros>): void {
    const siguiente = { ...local, ...parcial }
    setLocal(siguiente)
    onFiltros?.(siguiente)
  }

  /* Todos los hooks quedaron arriba: la salida por catálogo vacío va aquí, con
     el render, y no antes, porque el orden de los hooks no puede depender de
     los datos. */
  if (referencia === null) {
    return (
      <>
        <Cabecera />
        <Callout kind="warning" title="El catálogo de referencias llegó vacío">
          <p>
            La fuente se ingirió, pero no trae ninguna presentación de referencia. Sin presentación
            declarada no hay precio base que publicar: la selección la hace el pipeline y se
            verifica contra el archivo de Profeco en cada corrida.
          </p>
        </Callout>
      </>
    )
  }

  const unitLabel = unidadLarga(referencia)
  /* «tonelada/toneladas» o «litro/litros», según el denominador del precio. El
     rótulo del campo y el del veredicto salen de aquí para que no puedan
     discrepar del factor con que se calculó el total. */
  const rotuloBulto = OFERTA_BULTO[referencia.denominador]
  const celdaRango: CeldaDetalle | PriceCell | null = celdaPublicada ?? celdaDetalle

  /* El piso de observaciones que respeta el archivo de detalle, medido sobre
     sus propias filas. Es la razón que se le da al lector cuando un corte no
     tiene celda, y por eso se lee del archivo en vez de teclearse. */
  const pisoDetalle = indice?.piso ?? null

  /* --- Los tres textos de §4.6, con la cobertura real de la lista ---------
     Las tres frases dicen lo mismo desde tres lugares: cuántas entidades
     publica el archivo de las que tienen observación. Ninguna afirma un umbral
     de la vista, porque la vista no tiene ninguno; el único umbral que existe es
     el del pipeline y aquí se mide, no se declara. */
  const frasePiso =
    pisoDetalle === null
      ? ''
      : ` Ninguna celda de ese archivo baja de ${integer(pisoDetalle)} ${pluralize(pisoDetalle, 'observación', 'observaciones')}: por debajo de ese piso el pipeline no escribe cuantiles por entidad.`
  const tituloEntidades =
    entidadesConObservacion === null
      ? `${integer(porEntidad.length)} ${pluralize(porEntidad.length, 'entidad', 'entidades')}`
      : `${integer(porEntidad.length)} de ${integer(entidadesConObservacion)} ${pluralize(entidadesConObservacion, 'entidad', 'entidades')}`
  const vacioEntidades =
    entidadesConObservacion === null
      ? `El archivo de detalle no trae ninguna entidad de esta presentación en central de abasto en ${formatPeriodLong(mes)}.${frasePiso}`
      : `El archivo de detalle no publica ninguna de las ${integer(entidadesConObservacion)} ${pluralize(entidadesConObservacion, 'entidad', 'entidades')} con observación de esta presentación en central de abasto en ${formatPeriodLong(mes)}.${frasePiso}`
  const pieEntidades = `Fuente: Profeco, Quién es Quién en los Precios (CC BY 4.0), ${formatPeriodLong(mes)}.${
    entidadesConObservacion === null
      ? ''
      : ` De las ${integer(entidadesConObservacion)} ${pluralize(entidadesConObservacion, 'entidad con observación', 'entidades con observación')} en el mes, el archivo de detalle publica ${integer(porEntidad.length)}.${frasePiso}`
  }`

  return (
    <>
      {/* --- 4.0 Cabecera y vigencia ------------------------------------ */}
      <Cabecera descripcion />

      <p className="t-footnote ink-2 pb-vigencia">
        <strong>
          Datos al {formatDate(retail.lastObservation)} · {fraseAntiguedad(diasDeRezago)}.
        </strong>{' '}
        Profeco publica con rezago y por lotes irregulares; la última quincena disponible es la{' '}
        {quincenaLarga(retail.lastQuincena)}. El boletín del INEGI que usa el resto del sitio va en{' '}
        {formatPeriodLong(dataset.latestPeriod)}:{' '}
        <strong>
          no compare una cifra de esta pantalla con una de Panorama, son meses distintos.
        </strong>
      </p>

      {/* --- 4.1 La advertencia, antes de cualquier número --------------- */}
      <Callout kind="critical" title="Esto no es la canasta del INEGI">
        <p>
          Comparamos <strong>{integer(contraste.renglones)} renglones</strong> de esta fuente contra
          el boletín del INEGI de {formatPeriodLong(contraste.period)}: la diferencia mediana por
          renglón es de <strong>{percent(contraste.desviacionMedianaPct)}</strong> y sólo{' '}
          <strong>
            {integer(contraste.dentroDe10)} de {integer(contraste.renglones)}
          </strong>{' '}
          caen dentro de ±10 %. No es un error de ninguna de las dos. El INEGI mide el precio que
          paga un hogar en todos los canales —donde el comercio tradicional pesa mucho— y Profeco
          mide el anaquel del autoservicio y el mayoreo de la central.
          {tortilla !== null ? (
            <>
              {' '}
              La tortilla lo muestra sola: <strong>{currency(tortilla.tortilleria)}</strong> el kilo
              en tortillería, <strong>{currency(tortilla.anaquel)}</strong> en{' '}
              {tortilla.canalAnaquel}, <strong>{currency(tortilla.boletin)}</strong> en el boletín.
              Las tres cifras son correctas y miden cosas distintas.
            </>
          ) : null}
        </p>
        <p>
          Por eso esta pantalla <strong>nunca</strong> presenta una «canasta según Profeco», no suma
          renglones y no escala nada a un hogar. Da el precio de un producto concreto para poder
          comprarlo, y el diferencial contra el anaquel cuando ese diferencial existe. Nada más que
          eso.
        </p>
        <p className="t-caption ink-3">{RETAIL_NOT_CANASTA_WARNING}</p>
      </Callout>

      {/* --- 4.2 Filtros ------------------------------------------------- */}
      <div className="no-print">
        <FilterBar ariaLabel="Filtros del precio base">
          <FilterField label="Renglón de la canasta">
            <Select
              value={renglon}
              onChange={(v) => cambiar({ renglon: v, pres: null })}
              options={renglonOptions}
              ariaLabel="Renglón de la canasta"
            />
            {/* El grupo se rotula por posición y por conteo, no listando los
                nombres: los cinco rótulos del ENIGH juntos miden 180 caracteres
                («Aceite vegetal: canola, cártamo, girasol, maíz, etcétera») y
                empujaban la fila de filtros a tres renglones de alto. */}
            {grupoTonelada.length > 0 ? (
              <p className="t-caption ink-3 pb-hint">
                {GRUPO_TONELADA}: {integer(grupoTonelada.length)}{' '}
                {pluralize(grupoTonelada.length, 'renglón', 'renglones')} al principio de la lista.
              </p>
            ) : null}
          </FilterField>

          <FilterField label="Presentación de referencia">
            <Select
              value={refId}
              onChange={(v) => cambiar({ pres: v })}
              options={presOptions}
              ariaLabel="Presentación de referencia"
            />
            {/* La cadena literal de Profeco va en una segunda línea VISIBLE y no
                en un tooltip: es lo que permite auditar la presentación contra
                el archivo, y en una pantalla táctil un tooltip no existe. */}
            <p className="t-caption ink-3 pb-hint">«{referencia.presentacionQqp}»</p>
          </FilterField>

          <FilterField label="Entrega en">
            <Select
              value={entrega}
              onChange={(v) => cambiar({ entrega: v })}
              options={entregaOptions}
              ariaLabel="Entidad de entrega"
            />
            {estadoDetalle === 'cargando' ? (
              <p className="t-caption ink-3 pb-hint">Cargando las entidades con central…</p>
            ) : null}
          </FilterField>

          <FilterField label="Mes">
            <Select
              value={mes}
              onChange={(v) => cambiar({ mes: v })}
              options={mesOptions}
              ariaLabel="Mes de la observación"
            />
          </FilterField>
        </FilterBar>
      </div>

      {/* Un corte fuera de la banda publicada se declara UNA vez, aquí; las
          secciones que dependen de ella lo repiten en corto. */}
      {banda === null ? (
        <Callout kind="info" title="Este corte no tiene precio base publicado">
          <p>
            La compuerta de publicación, el intervalo de confianza de la mediana, el diferencial
            pareado contra el anaquel y la deriva los calcula el pipeline sobre el corte{' '}
            <strong>nacional</strong> del mes de referencia ({formatPeriodLong(mesReferencia)}).
            Para{' '}
            <strong>
              {rotuloEntrega}, {formatPeriodLong(mes)}
            </strong>{' '}
            {/* La promesa «y aquí están» va condicionada a que la celda exista.
                Sin condicionar, este párrafo afirmaba que los cuantiles estaban
                abajo mientras la tarjeta del hero decía «Cargando…» y la casilla
                del rango decía «Sin cuantiles»: tres afirmaciones simultáneas y
                contradictorias en la misma pantalla. */}
            {celdaDetalle !== null
              ? 'el archivo de detalle sí trae los cuantiles observados y aquí están, pero sin esas cuatro cosas: recalcularlas en la pantalla sería repetir en otro lado, y con otros umbrales, reglas que hoy viven en uno solo.'
              : estadoDetalle === 'listo'
                ? 'el archivo de detalle tampoco trae celda: este corte se quedó por debajo del piso de observaciones del pipeline, así que aquí no hay cuantiles que enseñar ni se toman prestados de otro mes.'
                : estadoDetalle === 'error'
                  ? 'el archivo de detalle no respondió, así que no se sabe si trae cuantiles de este corte.'
                  : 'el archivo de detalle todavía se está descargando; en cuanto llegue se verá si trae los cuantiles de este corte.'}
          </p>
          <p>
            {esNacional
              ? 'Vuelva al mes de referencia para ver el precio base, su compuerta y su ficha.'
              : celdaDetalle !== null
                ? 'Una celda de una sola entidad tampoco puede pasar la compuerta: ésta exige observaciones en tres entidades o más. Lo que sí contesta el corte por entidad está abajo, en «Dónde se levantó ese precio».'
                : 'Una celda de una sola entidad tampoco puede pasar la compuerta: ésta exige observaciones en tres entidades o más. Y sin celda no aparece tampoco abajo, en «Dónde se levantó ese precio»: esa lista se arma del mismo archivo.'}
          </p>
        </Callout>
      ) : null}

      {/* --- 4.3 El hero: la banda de precio ----------------------------
          En papel queda una hoja: cabecera, franja de vigencia, la advertencia
          del INEGI y la ficha. La banda es una gráfica y viaja en `.no-print`
          con las demás; sus cifras están íntegras en la ficha, así que el PDF
          no pierde nada auditable. */}
      <div className="no-print">
        {publicable && banda !== null && banda.central !== null ? (
          <div className="hero-card">
            <HeroFigure
              value={banda.central.p50}
              format={(v) => currency(v)}
              unit={porUnidad(referencia)}
              caption={`${referencia.label} · ${RETAIL_CHANNEL_LABEL.central.toLowerCase()} · ${formatPeriodLong(mes)}`}
            >
              <p className="t-footnote ink-2">
                {integer(banda.central.n)} {pluralize(banda.central.n, 'observación', 'observaciones')}{' '}
                en {integer(banda.central.states)}{' '}
                {pluralize(banda.central.states, 'entidad', 'entidades')} y{' '}
                {integer(banda.central.outlets)}{' '}
                {pluralize(banda.central.outlets, 'punto de venta', 'puntos de venta')}
              </p>
            </HeroFigure>
            <div className="hero-card__aside">
              <PriceBand
                cell={banda.central}
                label={referencia.label}
                markers={marcas}
                format={(v) => currency(v)}
                unitLabel={unitLabel}
                grade={banda.grade}
                gradeReason={banda.gradeReason}
                footnote={pieDeBanda(retail, mes, diasDeRezago)}
              />
            </div>
          </div>
        ) : banda !== null && banda.central !== null ? (
          <Card>
            <p className="t-footnote ink-2 pb-hero-caption">
              {referencia.label} · {RETAIL_CHANNEL_LABEL.central.toLowerCase()} ·{' '}
              {formatPeriodLong(mes)}
            </p>
            <PriceBand
              cell={banda.central}
              label={referencia.label}
              markers={marcas}
              format={(v) => currency(v)}
              unitLabel={unitLabel}
              grade={banda.grade}
              gradeReason={banda.gradeReason}
              footnote={pieDeBanda(retail, mes, diasDeRezago)}
            />
          </Card>
        ) : celdaDetalle !== null ? (
          <Card>
            <p className="t-footnote ink-2 pb-hero-caption">
              Precio observado · {referencia.label} · {RETAIL_CHANNEL_LABEL.central.toLowerCase()} ·{' '}
              {rotuloEntrega} · {formatPeriodLong(mes)}
            </p>
            <p className="figure-lg tabular">
              {currency(celdaDetalle.p25)} – {currency(celdaDetalle.p75)}
            </p>
            <p className="t-footnote ink-2">
              Mitad central de {integer(celdaDetalle.n)}{' '}
              {pluralize(celdaDetalle.n, 'observación', 'observaciones')} en{' '}
              {integer(celdaDetalle.outlets)}{' '}
              {pluralize(celdaDetalle.outlets, 'punto de venta', 'puntos de venta')}.
            </p>
            <dl className="deflist pb-cuantiles">
              <dt>10 % más barato</dt>
              <dd className="tabular">{currency(celdaDetalle.p10)}</dd>
              <dt>Mediana</dt>
              <dd className="tabular">{currency(celdaDetalle.p50)}</dd>
              <dt>10 % más caro</dt>
              <dd className="tabular">{currency(celdaDetalle.p90)}</dd>
            </dl>
            <p className="t-caption ink-3">
              Cuantiles observados, {unitLabel}. El archivo de detalle no publica el intervalo de
              confianza de la mediana, así que esta celda no se dibuja como banda de precio: no habría
              manera de decir cuánto de esa mediana es muestra.
            </p>
          </Card>
        ) : (
          /* Tres estados, no dos. Con `listo` y sin celda —el caso de frijol
             negro en Guanajuato en mayo— el `else` de dos ramas dejaba aquí
             «Cargando…» para siempre: la descarga ya había terminado y no había
             nada más que esperar. La ausencia se declara y se explica; nunca se
             disfraza de espera. */
          <Card>
            <p className="t-footnote ink-2" role="status">
              {estadoDetalle === 'error'
                ? `El archivo ${retail.detailFile} no respondió, así que este corte no tiene cuantiles que mostrar. El corte nacional del mes de referencia sí: viene del dataset principal y no depende de esa descarga.`
                : estadoDetalle === 'listo'
                  ? `El archivo ${retail.detailFile} ya llegó completo y no trae celda de ${RETAIL_CHANNEL_LABEL.central.toLowerCase()} para ${rotuloEntrega} en ${formatPeriodLong(mes)}.${pisoDetalle === null ? '' : ` Ninguna celda de ese archivo baja de ${integer(pisoDetalle)} ${pluralize(pisoDetalle, 'observación', 'observaciones')}: por debajo de ese piso el pipeline no escribe cuantiles, y este corte se quedó abajo.`} No hay nada que esperar, y estos cuantiles no se sustituyen con los de otro mes ni con los de otro corte.`
                  : 'Cargando los cuantiles de este corte…'}
            </p>
          </Card>
        )}
      </div>

      {/* --- 4.4 Las cuatro casillas ------------------------------------- */}
      <div className="no-print">
        <div className="grid grid--kpi">
          {/* `Casilla` y no `StatTile`: el rango es un PAR, no una magnitud.
              `StatTile` anima un solo número, así que el extremo bajo salía del
              resorte —valor viejo en vuelo— y el alto se leía fresco del render.
              Medido al cambiar de pierna de cerdo (p25 $185.00) a arroz grande
              (p25 $17.50, p75 $27.00): la casilla imprimía «$185.00 – $27.00»,
              un rango invertido, y en saltos más chicos parejas plausibles de
              dos productos distintos. Se animan los dos extremos o ninguno, y
              aquí ninguno: ver contar un par no aporta nada y de paso la cifra
              deja de partirse en dos renglones, que es lo que hacía a 2 rem en
              una casilla de 200 px. */}
          {celdaRango !== null ? (
            <Casilla
              label="Rango de negociación"
              valor={`${currency(celdaRango.p25)} – ${currency(celdaRango.p75)}`}
              nota={`Mitad central de las ${integer(celdaRango.n)} ${pluralize(celdaRango.n, 'observación', 'observaciones')}.`}
            />
          ) : (
            <Casilla label="Rango de negociación" valor="—" nota="Sin cuantiles para este corte." />
          )}

          {margen !== null && margenPct !== null ? (
            <StatTile
              label="Contra el anaquel"
              value={margenPct}
              format={(v) => percentDelta(v)}
              note={`${integer(margen.states)} ${pluralize(margen.states, 'entidad pareada', 'entidades pareadas')}; el intervalo del 95 % va de ${pctOsinDato(margen.ciLo)} a ${pctOsinDato(margen.ciHi)}.`}
            />
          ) : (
            <Casilla
              label="Contra el anaquel"
              valor="—"
              nota={
                margen?.reason ??
                'El diferencial contra el anaquel se publica sobre el corte nacional del mes de referencia.'
              }
            />
          )}

          {/* La cifra pasa cuando la serie es plana AUNQUE el R² sea 0.00: en
              esas cuatro referencias el 0.00 es 0/0 y no un mal ajuste. El 0.0 %
              que se imprime es el dato, no un relleno —la mediana no se movió— y
              la chispa lo confirma con una recta horizontal. */}
          {deriva !== null && derivaR2 !== null && (estable !== null || derivaR2 >= DERIVA_R2_MINIMO) ? (
            <StatTile
              label="Se mueve"
              value={deriva}
              format={(v) => percentDelta(v)}
              unit="al mes"
              trend={tendencia}
              note={`${fraseDeriva(deriva, derivaR2, primerMesSerie, estable)}${esCortePublicado ? '' : ` ${DERIVA_ES_NACIONAL}`}`}
            />
          ) : (
            /* La chispa se dibuja siempre que haya serie: ahora la deriva de la
               casilla es la nacional en los cuatro cortes, así que la curva y la
               frase hablan del mismo lugar. */
            <Casilla
              label="Se mueve"
              valor="sin tendencia clara"
              nota={`${fraseDeriva(deriva, derivaR2, primerMesSerie, estable)}${esCortePublicado ? '' : ` ${DERIVA_ES_NACIONAL}`}`}
              trend={tendencia}
            />
          )}

          {/* Con la ventana agotada el valor es una fecha PASADA y lo dice: la
              casilla imprimía «revisar antes del 10 de agosto» sobre una papa
              cuya vigencia de 8 días se acabó el 6 de junio. Las tres redacciones
              —ésta, la del acta y la del CSV— salen de `prosaVigencia`. */}
          <Casilla
            label="Vigencia sugerida"
            valor={prosaVig.valor}
            nota={`${prosaVig.nota}${esCortePublicado ? '' : ` ${vigenciaEsNacional(mesReferencia)}`}`}
          />
        </div>
      </div>

      {/* --- 4.5 Califique una oferta ------------------------------------ */}
      <div className="no-print">
        <Section
          title="Califique una oferta"
          description="Se compara contra los cinco cuantiles de la celda, del lado del cliente. Nada de lo que escriba aquí sale de su navegador."
        >
          <Card>
            {celdaCalificable === null ? (
              <p className="t-callout ink-2">
                {banda === null
                  ? `El evaluador trabaja contra el precio base publicado, y este corte no tiene uno. Vuelva a Nacional y a ${formatPeriodLong(mesReferencia)}.`
                  : OFERTA_SIN_BASE}
              </p>
            ) : (
              <div className="stack">
                <p className="t-callout pb-oferta">
                  <span>Me ofrecen</span>
                  <span className="pb-campo">
                    <span className="pb-campo__signo" aria-hidden="true">
                      $
                    </span>
                    <input
                      className="pb-input t-callout tabular"
                      type="text"
                      inputMode="decimal"
                      value={oferta}
                      onChange={(e) => setOferta(e.target.value)}
                      aria-label={`Precio que le ofrecen, ${unitLabel}`}
                      placeholder="0.00"
                      maxLength={OFERTA_MAX_CARACTERES}
                    />
                  </span>
                  <span>{porUnidad(referencia)}, para</span>
                  <input
                    className="pb-input pb-input--corto t-callout tabular"
                    type="text"
                    inputMode="decimal"
                    value={bulto}
                    onChange={(e) => setBulto(e.target.value)}
                    aria-label={`${capitalizar(rotuloBulto.plural)} de la compra`}
                    placeholder="0"
                    maxLength={OFERTA_MAX_CARACTERES}
                  />
                  {/* Sin bulto declarado la palabra va en plural, que es la
                      forma neutra de la oración; no se supone «1 tonelada». */}
                  <span>
                    {bultoNum === null
                      ? rotuloBulto.plural
                      : pluralize(bultoNum, rotuloBulto.singular, rotuloBulto.plural)}
                    .
                  </span>
                </p>

                {/* El comprador de aceite piensa en toneladas y aquí se le pide
                    otra cosa: se le dice por qué antes de que lo teclee. */}
                {referencia.denominador === 'L' ? (
                  <p className="t-footnote ink-2">{OFERTA_BULTO_LITROS}</p>
                ) : null}

                {/* Región viva del veredicto, en un envoltorio ESTABLE: existe
                    también cuando no hay calificación, así que el lector anuncia
                    el CAMBIO de contenido y no el montaje de un nodo —un
                    `role="status"` que aparece junto con su texto no se anuncia
                    de forma confiable—. El evaluador es la única parte
                    interactiva de la vista y el foco se queda en el campo:
                    tecleando el precio no se oía ni el veredicto, ni el ahorro,
                    ni el fundamento, y no había manera de saber que había
                    aparecido algo. Calca `AbastoDirectorio.tsx:274`. */}
                <div role="status">
                  {calificacion !== null && ofertaNum !== null ? (
                    <div className={`pb-veredicto pb-veredicto--${calificacion.tier}`}>
                      <p className="t-callout">
                        <strong>{calificacion.title}.</strong> {currency(ofertaNum)}:{' '}
                        {calificacion.text} En central de abasto el 25 % más barato empieza en{' '}
                        <span className="tabular">{currency(celdaCalificable.p25)}</span> y la mediana
                        está en <span className="tabular">{currency(celdaCalificable.p50)}</span>.
                        {/* Sin bulto declarado no hay total y no se dice nada: no
                            se supone una tonelada. Con bulto declarado y sin
                            total —el producto desbordó— la ausencia se declara,
                            que es la regla de esta pantalla; antes se imprimía
                            «cuestan $∞ más». */}
                        {calificacion.bulk !== null ? (
                          calificacion.totalDelta === null ? (
                            <>
                              {' '}
                              Contra el precio base no hay total que dar: el precio por la cantidad
                              rebasa el mayor número que se puede representar. Corrija la oferta o la
                              cantidad.
                            </>
                          ) : (
                            <>
                              {' '}
                              Contra el precio base, {number(calificacion.bulk, 0)}{' '}
                              {pluralize(calificacion.bulk, rotuloBulto.singular, rotuloBulto.plural)}{' '}
                              {/* El verbo concuerda con el bulto: «1 tonelada cuesta»,
                                  «10 toneladas cuestan». Con un solo litro la oración
                                  es igual de legítima que con mil. */}
                              {calificacion.totalDelta === 0 ? (
                                `${pluralize(calificacion.bulk, 'cuesta', 'cuestan')} lo mismo.`
                              ) : (
                                <>
                                  {pluralize(calificacion.bulk, 'cuesta', 'cuestan')}{' '}
                                  <strong>
                                    {currency(Math.abs(calificacion.totalDelta), 0)}{' '}
                                    {calificacion.totalDelta < 0 ? 'menos' : 'más'}
                                  </strong>
                                  .
                                </>
                              )}
                            </>
                          )
                        ) : null}
                      </p>
                      <p className="t-footnote ink-2">
                        <em>
                          Fundamento: {integer(celdaCalificable.n)}{' '}
                          {pluralize(celdaCalificable.n, 'observación', 'observaciones')} en{' '}
                          {integer(celdaCalificable.states)}{' '}
                          {pluralize(celdaCalificable.states, 'entidad', 'entidades')} y{' '}
                          {integer(celdaCalificable.outlets)}{' '}
                          {pluralize(celdaCalificable.outlets, 'punto de venta', 'puntos de venta')},{' '}
                          {formatPeriodLong(mes)}. La mitad central de esas observaciones va de{' '}
                          {currency(celdaCalificable.p25)} a {currency(celdaCalificable.p75)}: una
                          oferta dentro de ese rango es precio de mercado, no un logro ni un abuso.
                        </em>
                      </p>
                      {/* La comparación contra el anaquel aparece SÓLO si el
                          veredicto del margen trae número: sin él, la mediana de
                          anaquel existe pero no significa lo que una resta puesta
                          junto al precio base haría creer. */}
                      {margenPct !== null &&
                      margen !== null &&
                      banda !== null &&
                      banda.anaquel !== null ? (
                        <p className="t-footnote ink-2">
                          En {rotuloAnaquel} la mediana de la misma presentación es{' '}
                          {currency(banda.anaquel.p50)}, y el diferencial pareado por entidad es de{' '}
                          {percentDelta(margenPct)} sobre {integer(margen.states)}{' '}
                          {pluralize(margen.states, 'entidad', 'entidades')}.
                        </p>
                      ) : null}
                    </div>
                  ) : (
                    <p className="t-footnote ink-3">
                      {/* «La cantidad» y no «el tonelaje»: con denominador `L` el
                          campo pide litros, y prometer toneladas aquí volvería a
                          sugerir la conversión que R11 prohíbe. */}
                      Escriba el precio que le ofrecen para calificarlo. La cantidad es opcional: sin
                      ella sólo se puede hablar de pesos {porUnidad(referencia)}, no de la compra
                      completa.
                    </p>
                  )}
                </div>

                <p className="t-caption ink-3">{OFERTA_LIMITES}</p>
              </div>
            )}
          </Card>
        </Section>
      </div>

      {/* --- 4.6 Dónde se levantó ese precio ----------------------------- */}
      <div className="no-print">
        <Section
          title="Dónde se levantó ese precio"
          description="El mismo producto, la misma presentación, el mismo mes. La diferencia es geografía, no calidad."
        >
          {estadoDetalle === 'error' ? (
            <Callout kind="warning" title="No se pudo cargar el desglose por entidad">
              <p>
                El archivo <code>{retail.detailFile}</code> no respondió. El precio base, su rango y
                su ficha sí están: vienen del dataset principal y no dependen de esta descarga.
              </p>
            </Callout>
          ) : (
            <>
              <Card>
                <BarList
                  rows={porEntidad}
                  /* El título lleva las dos cifras porque el hero, cuatro
                     secciones arriba, ya dijo en cuántas entidades se levantó el
                     precio: «5 de 11» cierra la contradicción de un vistazo. */
                  title={tituloEntidades}
                  subtitle="El mismo producto, la misma presentación, el mismo mes. La diferencia es geografía, no calidad."
                  nameHeading="Entidad"
                  caption={`Precio mediano en central de abasto por entidad y su desviación contra la mediana nacional, ${unitLabel}.`}
                  valueHeading="Precio"
                  magnitudeHeading="Contra la mediana nacional"
                  formatValue={(v) => currency(v)}
                  formatMagnitude={(v) => percentDelta(v)}
                  mode="diverging"
                  magnitudeKind="percent"
                  valueKind="currency"
                  emptyMessage={
                    estadoDetalle === 'listo' ? vacioEntidades : 'Cargando el desglose por entidad…'
                  }
                  footnote={pieEntidades}
                />
              </Card>

              {/* El aviso se sigue disparando sobre las entidades PUBLICADAS y
                  eso es válido: el rango de un subconjunto nunca es mayor que el
                  del conjunto, así que 281.8 % medido sobre 5 de 11 es un piso
                  del rango verdadero, no una estimación de él. Lo que no era
                  válido es presentarlo como si cubriera el país —el jalapeño
                  nombraba Nuevo León y Yucatán como «la entidad más barata y la
                  más cara» siendo extremos de 5 de 11, y a057-granel-pierna
                  dispara con 2 de 7—, así que la frase se acota a lo medido y
                  la segunda línea dice de cuántas salió. */}
              {dispersion !== null && dispersion.pct > DISPERSION_MAX_PCT ? (
                <Callout kind="warning" title="No convoque a precio nacional">
                  <p>
                    Entre la más barata y la más cara de las{' '}
                    {integer(porEntidad.length)}{' '}
                    {pluralize(porEntidad.length, 'entidad', 'entidades')} que el archivo publica hay{' '}
                    <strong>{percent(dispersion.pct)}</strong>: {currency(dispersion.barata.value)}{' '}
                    en {dispersion.barata.label} contra {currency(dispersion.cara.value)} en{' '}
                    {dispersion.cara.label}. Un precio base nacional deja fuera a los proveedores de{' '}
                    {dispersion.cara.label} o regala margen en {dispersion.barata.label}. Convoque
                    por región.
                  </p>
                  {entidadesConObservacion !== null &&
                  entidadesConObservacion > porEntidad.length ? (
                    <p className="t-footnote ink-2">
                      Son los extremos de {integer(porEntidad.length)} de las{' '}
                      {integer(entidadesConObservacion)} entidades con observación en el mes: de las
                      otras {integer(entidadesConObservacion - porEntidad.length)} el archivo de
                      detalle no publica celda. Sumarlas sólo puede separar más los extremos, nunca
                      acercarlos, así que la brecha entre entidades no es menor que ésta.
                    </p>
                  ) : null}
                </Callout>
              ) : null}
            </>
          )}
        </Section>
      </div>

      {/* --- 4.7 Contra el anaquel --------------------------------------- */}
      <div className="no-print">
        <Section
          title="Qué tanto más caro es el mismo producto en el súper"
          description="Primero el veredicto. El número sólo aparece cuando la misma presentación existe en los dos canales, representa el mismo bien y los dos canales coinciden en suficientes entidades."
        >
          <Card>
            {margen === null ? (
              <p className="t-callout ink-2">
                El diferencial contra el anaquel se calcula sobre el corte nacional del mes de
                referencia ({capitalizar(formatPeriodLong(mesReferencia))}). Este corte no lo tiene.
              </p>
            ) : margen.verdict === 'sin-paridad-de-canal' ? (
              <div className="stack stack--tight">
                <p className="t-callout">
                  <strong>No hay comparación posible para esta presentación.</strong>{' '}
                  {margen.reason}
                </p>
                {alternaConMargen !== null ? (
                  <p className="t-footnote ink-2">
                    Para ver el diferencial contra anaquel, elija{' '}
                    <button
                      type="button"
                      className="link-button"
                      onClick={() => cambiar({ pres: alternaConMargen.id })}
                    >
                      «{alternaConMargen.label}»
                    </button>
                    .
                  </p>
                ) : null}
                {tonelada.sinCanal > 1 ? (
                  <p className="t-footnote ink-3">
                    <em>
                      Le pasa lo mismo a {integer(tonelada.sinCanal)} de las{' '}
                      {integer(tonelada.total)} presentaciones de los renglones que BAMX compra por
                      tonelada: el autoservicio no vende ese bien a granel.
                    </em>
                  </p>
                ) : null}
              </div>
            ) : margen.verdict === 'sin-paridad-de-calidad' ? (
              <p className="t-callout">
                <strong>El diferencial existe, pero no es un margen.</strong> {margen.reason}
              </p>
            ) : margen.verdict === 'sin-pareo-suficiente' ? (
              <p className="t-callout">
                <strong>Sin entidades suficientes para comparar.</strong> {margen.reason}
              </p>
            ) : margen.verdict === 'sin-diferencia' ? (
              <p className="t-callout">
                <strong>Sin diferencia medible.</strong> El diferencial estimado es de{' '}
                <strong>{pctOsinDato(margenPct)}</strong>, pero con{' '}
                <strong>
                  {integer(margen.states)}{' '}
                  {pluralize(margen.states, 'entidad pareada', 'entidades pareadas')}
                </strong>{' '}
                el intervalo de confianza del 95 % va de{' '}
                <strong>
                  {pctOsinDato(margen.ciLo)} a {pctOsinDato(margen.ciHi)}
                </strong>{' '}
                e incluye el cero. Con esta muestra no se puede afirmar que la central sea más
                barata en este renglón.{' '}
                <strong>
                  Es el caso de {integer(conteoVeredictos.sinDiferencia)} de las{' '}
                  {integer(conteoVeredictos.conNumero)} presentaciones donde el diferencial se puede
                  calcular.
                </strong>
              </p>
            ) : margenPct === null ? (
              /* Inalcanzable con un dataset válido: `validate.ts` exige que los
                 dos veredictos con número traigan `pct`. Existe porque la
                 alternativa era un `?? 0`, y un cero aquí imprimiría «cuesta
                 0.0 % más», que es una afirmación y no una ausencia. */
              <p className="t-callout ink-2">
                {margen.reason ?? 'El veredicto trae número, pero el diferencial no llegó en el dataset.'}
              </p>
            ) : (
              <div className="stack stack--tight">
                <p className="t-callout">
                  {referencia.label} cuesta{' '}
                  <strong>
                    {percent(Math.abs(margenPct))} {margenPct > 0 ? 'más' : 'menos'}
                  </strong>{' '}
                  en {rotuloAnaquel} que en la central de abasto (IC 95 %:{' '}
                  {pctOsinDato(margen.ciLo)} a{' '}
                  {pctOsinDato(margen.ciHi)}, {integer(margen.states)}{' '}
                  {pluralize(margen.states, 'entidad pareada', 'entidades pareadas')}).
                </p>
                {margen.verdict === 'central-mas-cara' ? (
                  <p className="t-footnote ink-2">
                    No es un error de signo: hay presentaciones que el autoservicio vende como
                    producto de gancho, por debajo de lo que cuestan en la central.
                  </p>
                ) : null}
                <p className="t-caption ink-3">
                  El diferencial se calcula pareando por entidad: en cada entidad con muestra en los
                  dos canales se toma el cociente de medianas, y lo que se publica es la mediana de
                  esos cocientes con intervalo por remuestreo sobre entidades. Sin parear, la cifra
                  se sesga hasta decenas de puntos porcentuales.
                </p>
              </div>
            )}
          </Card>

          {/* El ranking va sólo cuando el veredicto trae número: comparar
              cadenas donde el bien no es el mismo ordena catálogos, no precios. */}
          {margenPct !== null ? (
            <Card>
              {!traslape.suficiente ? (
                <p className="t-callout ink-2">
                  <strong>No hay cadenas comparables para esta presentación.</strong> Dos cadenas
                  sólo se comparan sobre la misma presentación y en las entidades donde las dos la
                  venden; si una no la cotiza en una entidad, esa entidad sale del cálculo de todas,
                  porque si no una cadena saldría barata por no vender el producto. Aquí quedan{' '}
                  <strong>
                    {integer(traslape.total)}{' '}
                    {pluralize(traslape.total, 'entidad común', 'entidades comunes')}
                  </strong>
                  , menos de las <strong>{integer(traslape.minimo)}</strong> que exigimos para
                  publicar un orden. Un ranking con menos entidades le echa a la cadena la culpa de
                  dónde tiene tiendas.
                </p>
              ) : (
                <>
                  <BarList
                    rows={filasRanking}
                    /* El título cuenta CADENAS, no filas: la primera fila es la
                       central de abasto y no es una cadena de autoservicio. */
                    title={`${integer(cadenasEnRanking)} ${pluralize(cadenasEnRanking, 'cadena', 'cadenas')} contra la central`}
                    /* El subtítulo hablaba por TODAS las filas —«sólo en las N
                       entidades donde todas las cadenas cotizan»— y la fila
                       ancla no cumplía esa base. Ahora la cumple, pero sobre
                       menos entidades de las N, y eso se dice aquí en vez de
                       dejar que la frase de las cadenas la cubra. */
                    subtitle={`Mediana por entidad y luego mediana entre entidades, sólo en las ${integer(traslape.total)} entidades donde todas las cadenas del ranking cotizan esta presentación. Una cadena que sólo opera en el centro-sur no se compara contra una nacional. ${
                      anclaRanking === null
                        ? 'La central de abasto no cotiza esta presentación en ninguna de ellas, así que el ranking va sin fila ancla.'
                        : `La central de abasto se mide con la misma regla y sobre las mismas entidades, aunque sólo cotiza en ${integer(anclaRanking.n)} de ellas.`
                    }`}
                    nameHeading="Cadena"
                    caption={
                      anclaRanking === null
                        ? `Precio mediano de ${referencia.label} por cadena, ${unitLabel}.`
                        : `Precio mediano de ${referencia.label} por cadena y su distancia contra la central de abasto, ${unitLabel} y por ciento.`
                    }
                    valueHeading="Precio"
                    /* «Comparación» prometía una segunda cifra y repetía el
                       precio. La columna dice ahora contra qué se compara y trae
                       la distancia contra la fila ancla. */
                    magnitudeHeading="Contra la central"
                    formatValue={(v) => currency(v)}
                    formatMagnitude={(v) => percentDelta(v)}
                    /* `diverging` y no `magnitude`: la magnitud trae signo, y el
                       modo de magnitud dibuja el valor absoluto —una cadena 40 %
                       más barata y otra 40 % más cara saldrían con la misma
                       barra—. Es el mismo modo, y por el mismo motivo, que la
                       lista de entidades de §4.6. */
                    mode="diverging"
                    magnitudeKind="percent"
                    valueKind="currency"
                    footnote={`Fuente: Profeco, Quién es Quién en los Precios (CC BY 4.0), ${formatPeriodLong(mes)}.${
                      anclaRanking === null
                        ? ''
                        : ' La primera fila es la central de abasto: es el ancla contra la que se leen las demás, no una competidora más.'
                    }`}
                  />
                  {/* La fila ancla ya NO es la celda del corte —esa es la del
                      precio base de arriba, agregada sobre otras entidades—,
                      así que la compuerta R4 no la gobierna y la nota vieja
                      sobre `publicable` dejó de aplicar. Lo que sí hay que
                      declarar es que la misma central aparece dos veces en la
                      pantalla con dos cifras distintas: sin esta línea el
                      lector concluiría que una de las dos está mal. */}
                  {anclaRanking === null ? (
                    <p className="t-caption ink-3">
                      La central de abasto no cotiza esta presentación en ninguna de las{' '}
                      {integer(traslape.total)} entidades comunes, así que el ranking no lleva ancla
                      y sólo ordena cadenas entre sí. El contraste contra la central es el
                      diferencial pareado de aquí arriba.
                    </p>
                  ) : celdaPublicada !== null && celdaPublicada.p50 !== anclaRanking.p50 ? (
                    <p className="t-caption ink-3">
                      El precio base de arriba y esta fila son la misma central medida sobre
                      entidades distintas: {currency(celdaPublicada.p50)} sale de{' '}
                      {integer(celdaPublicada.states)}{' '}
                      {pluralize(celdaPublicada.states, 'entidad del corte', 'entidades del corte')}{' '}
                      y {currency(anclaRanking.p50)} de {integer(anclaRanking.n)}{' '}
                      {pluralize(anclaRanking.n, 'entidad común', 'entidades comunes')},{' '}
                      {pluralize(
                        anclaRanking.n,
                        'que es la única donde la comparación contra las cadenas se sostiene',
                        'que son las únicas donde la comparación contra las cadenas se sostiene',
                      )}
                      . Para convocar se usa la de arriba; para leer el ranking, ésta.
                    </p>
                  ) : null}
                  <p className="t-caption ink-3">{RETAIL_OVERLAP_WARNING}</p>
                </>
              )}
            </Card>
          ) : null}
        </Section>
      </div>

      {/* --- 4.8 Cómo se ha movido --------------------------------------- */}
      <div className="no-print">
        <Section
          title="Cómo se ha movido"
          description="Mediana mensual de la presentación de referencia, por canal."
        >
          <Card>
            {/* Sin `role="status"`, igual que la pista gemela de `Panorama.tsx`.
                `LineChart` ya trae su propia región viva y anuncia el mes
                recorrido; con las dos, cada flecha del teclado disparaba dos
                anuncios del mismo dato con dos redacciones —«Mayo de 2026» aquí
                y «Mayo 2026» allá—, que en una cola `polite` no se leen como eco
                sino como dos cifras distintas. La pista sigue visible, que es su
                función. */}
            <p className="t-footnote ink-2 hero-scrub-hint">
              {mesScrub !== null && valoresScrub.length > 0
                ? `${capitalizar(formatPeriodLong(mesScrub))}: ${valoresScrub.join(' · ')}`
                : 'Arrastre sobre la gráfica para leer un mes.'}
            </p>
            {seriesGrafica.length === 0 ? (
              <p className="t-footnote ink-3">
                Esta presentación no tiene dos meses con observaciones suficientes en ningún canal:
                no hay curva que dibujar y no se interpola.
              </p>
            ) : (
              <LineChart
                series={seriesGrafica}
                title={referencia.label}
                subtitle={`${capitalizar(unitLabel)}. La serie es nacional: la fuente publica el desglose por entidad sólo del mes, no de la serie, así que «Entrega en» no la mueve.`}
                formatValue={(v) => currency(v)}
                unitLabel={unitLabel}
                height={280}
                hRules={reglas}
                annotations={huecos}
                onScrub={setMesScrub}
                footnote="Cada punto es la mediana mensual de la presentación de referencia. Los meses sin observaciones suficientes no se dibujan ni se interpolan."
              />
            )}
          </Card>
        </Section>
      </div>

      {/* --- 4.9 Ficha para el acta -------------------------------------- */}
      <Section
        title="Ficha para el acta"
        description="Todo lo que sostiene la cifra, en una hoja. Cada dato sale del dataset: no hay una sola cifra escrita a mano."
        actions={
          ficha.length > 0 ? (
            <div className="row no-print">
              <button type="button" className="pb-boton t-footnote" onClick={descargarFicha}>
                Descargar ficha (CSV)
              </button>
              <button type="button" className="pb-boton t-footnote" onClick={() => window.print()}>
                Imprimir ficha
              </button>
            </div>
          ) : null
        }
      >
        <Card className="acta">
          {ficha.length === 0 ? (
            <p className="t-callout ink-2">
              La ficha se emite sobre el corte que el pipeline publica como precio base: nacional,{' '}
              {formatPeriodLong(mesReferencia)}. Vuelva a ese corte para emitirla.
            </p>
          ) : (
            <>
              <p className="t-title3">Ficha de precio de referencia</p>
              <p className="t-footnote ink-2 pb-ficha__emision">
                Emitida el {formatDate(hoyIso)} por canasta-bamx.pages.dev
              </p>
              <dl className="deflist pb-ficha">
                {ficha.map(([campo, valor]) => (
                  <Fragment key={campo}>
                    <dt>{campo}</dt>
                    <dd>{valor}</dd>
                  </Fragment>
                ))}
              </dl>
            </>
          )}
        </Card>
      </Section>
    </>
  )
}
